import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  Avatar,
  ThemeProvider,
  createTheme,
  AppBar,
  Toolbar,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Divider,
  Chip,
  CircularProgress,
  TextField,
  Select,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Grid,
  Alert,
  Snackbar,
  keyframes
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CloseIcon from '@mui/icons-material/Close';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
// 模型方品牌官方 logo(simple-icons via react-icons,CC0 授权)
//   Veo / Nano Banana → Google 的 G(Veo)/ Gemini 钻石(Nano Banana)
//   Sora / GPT Image  → 同属 OpenAI,统一用 OpenAI 涡旋
//   Grok              → xAI / X
//   Seedance          → ByteDance(Doubao 即字节跳动)
//
// react-icons 5.x 的 IconType 返回 React.ReactNode,React 19 的 JSX 要求是
// Element | null —— 类型不兼容。统一 cast 成 ComponentType 解决,功能完全等价。
import {
  SiGoogle as _SiGoogle,
  SiGooglegemini as _SiGooglegemini,
  SiOpenai as _SiOpenai,
  SiX as _SiX,
  SiBytedance as _SiBytedance,
} from 'react-icons/si';
import ThreeDCarousel from './ThreeDCarousel';
import { useAuth } from './AuthContext';
import { PointsCheckDialog } from './shared/PointsCheckDialog';
import { usePointsCheck } from './shared/usePointsCheck';
import { TopNavBar } from './shared/TopNavBar';
import { API_BASE } from './config';

// 见上方注释:react-icons 5.x IconType 在 React 19 JSX 下需要 cast 成 ComponentType
type BrandIconProps = { size?: number | string; color?: string };
const SiGoogle = _SiGoogle as unknown as React.ComponentType<BrandIconProps>;
const SiGooglegemini = _SiGooglegemini as unknown as React.ComponentType<BrandIconProps>;
const SiOpenai = _SiOpenai as unknown as React.ComponentType<BrandIconProps>;
const SiX = _SiX as unknown as React.ComponentType<BrandIconProps>;
const SiBytedance = _SiBytedance as unknown as React.ComponentType<BrandIconProps>;

/** 外部 CDN 视频通过后端代理,避免 CORS / 防盗链 / 跨区 CDN 偶发抖动。
 *
 * aliyuncs.com OSS 不走代理:OSS 私有桶 + 签名 URL 浏览器 <video> 直接可播,
 * 而走代理会因为 video 标签不带 Bearer 头被后端 401 → 浏览器 "Format error"。
 * 真正需要代理的是有 Referer 防盗链的源(豆包 / 字节系)。 */
function toProxiedVideoUrl(url: string): string {
  if (!url) return url;
  try {
    const { hostname } = new URL(url);
    if (
      hostname.endsWith('doubao.com') ||
      hostname.endsWith('bytedance.com') ||
      hostname.endsWith('byteimg.com') ||
      hostname.endsWith('tiktokcdn.com') ||
      hostname.endsWith('nmslb.com')          // wuyinkeji Grok 视频 CDN
    ) {
      return `${API_BASE}/api/proxy/ext-video?url=${encodeURIComponent(url)}`;
    }
  } catch {}
  return url;
}

// 带加载占位 + 自动重试 + 手动重试的视频组件
//   - 首次 onError 不立即显示失败:等 1.5s 自动 reload 一次,最多 2 次
//   - 全部自动重试都失败后,显示"加载失败 + 重试"按钮让用户手动再试
//   - 手动重试 = 重新挂载 video(改 key 强制 remount,绕过 video element 内部锁死状态)
const MAX_AUTO_RETRY = 2;
// 上游 wuyinkeji 的两条 Grok 通道(grok-10s 旧通道 / grok-30s 图生视频)账号池不稳定时,
// 把此开关设为 true 临时置灰这两个档的生成按钮;上游恢复后改回 false 重新部署即可。
const WUYINKEJI_GROK_DISABLED = false;
const VideoWithLoader: React.FC<{
  src: string;
  autoPlay?: boolean;
  loop?: boolean;
  style?: React.CSSProperties;
}> = ({ src, autoPlay = false, loop = true, style }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // attempt 用作 video 的 key — 每次 ++ 会重新挂载 <video>,触发干净的重新加载
  const [attempt, setAttempt] = useState(0);
  const autoRetryCountRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const proxiedSrc = toProxiedVideoUrl(src);

  // src 变化(切换不同视频)时重置所有状态
  useEffect(() => {
    setLoading(true);
    setError(null);
    setAttempt(0);
    autoRetryCountRef.current = 0;
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, [src]);

  // 卸载时清理定时器
  useEffect(() => () => {
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
  }, []);

  const handleError = () => {
    if (autoRetryCountRef.current < MAX_AUTO_RETRY) {
      autoRetryCountRef.current += 1;
      // 不立即显示错误,等 1.5s 后悄悄重试一次
      retryTimerRef.current = setTimeout(() => {
        setLoading(true);
        setAttempt(a => a + 1);
      }, 1500);
    } else {
      setError('failed');
    }
  };

  const handleManualRetry = () => {
    autoRetryCountRef.current = 0;
    setError(null);
    setLoading(true);
    setAttempt(a => a + 1);
  };

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      {/* 加载态占位:深色卡片 + 微扫光 + 文字 */}
      {(loading && !error) && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#0b1220',
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0) 100%)',
              transform: 'skewX(-20deg)',
              animation: `${shine} 1.8s linear infinite`,
            }
          }}
        >
          <Typography
            variant="caption"
            sx={{
              position: 'relative',
              zIndex: 1,
              color: 'rgba(255,255,255,0.85)',
              letterSpacing: 1,
              animation: `${blink} 2.4s ease-in-out infinite`,
            }}
          >
            {autoRetryCountRef.current > 0 ? `重新加载中... (${autoRetryCountRef.current}/${MAX_AUTO_RETRY})` : '加载中...'}
          </Typography>
        </Box>
      )}

      {/* 错误占位 + 重试按钮 */}
      {error && (
        <Box sx={{
          position: 'absolute', inset: 0,
          display: 'flex', flexDirection: 'column', gap: 1,
          alignItems: 'center', justifyContent: 'center',
          bgcolor: '#1e293b',
        }}>
          <Typography variant="caption" color="error.main">视频加载失败</Typography>
          <Box
            component="button"
            onClick={handleManualRetry}
            sx={{
              border: '1px solid rgba(255,255,255,0.25)',
              background: 'transparent',
              color: 'rgba(255,255,255,0.85)',
              fontSize: 12,
              padding: '4px 14px',
              borderRadius: 999,
              cursor: 'pointer',
              '&:hover': { background: 'rgba(255,255,255,0.08)' },
            }}
          >
            重试
          </Box>
        </Box>
      )}

      <video
        key={attempt}
        controls
        autoPlay={autoPlay}
        loop={loop}
        style={style}
        src={proxiedSrc}
        playsInline
        onLoadedData={() => setLoading(false)}
        onCanPlay={() => setLoading(false)}
        onError={handleError}
      />
    </Box>
  );
};

const theme = createTheme({
  palette: {
    primary: { main: '#2563eb' },
    secondary: { main: '#10b981' },
    background: { default: '#f5f8fc' },
  },
  shape: { borderRadius: 12 },
  typography: {
    fontFamily: '-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,Helvetica Neue,Arial,sans-serif',
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    subtitle2: { fontWeight: 500 },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: { transition: 'box-shadow .25s, transform .25s' }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          // 保持原有圆角设置，不进行修改
        },
        containedPrimary: {
          // 微光按钮：纯正蓝色渐变 + 内发光 + 同色系投影
          background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
          boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
          transition: 'all 0.3s ease',
          '&:hover': {
            background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
            boxShadow: '0 8px 20px rgba(37, 99, 235, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
            transform: 'translateY(-1px)'
          }
        }
      }
    }
  }
});

// 扫光动画
const shine = keyframes`
  0% { left: -100%; }
  100% { left: 200%; }
`;

// 边框流光动画
const borderMove = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

// 运行态：闪烁文字
const blink = keyframes`
  0%, 100% { opacity: 0.25; }
  50% { opacity: 1; }
`;

// 轻量卡片通用样式
const cardBase = {
  p: 3,
  // 增加通透感
  bgcolor: 'rgba(255, 255, 255, 0.75)',
  backdropFilter: 'blur(20px) saturate(180%)',
  border: '1px solid rgba(255, 255, 255, 0.6)',
  // Deep Elevation: 大扩散、低透明度的双层阴影，营造悬浮感
  boxShadow: '0 20px 40px -4px rgba(37, 99, 235, 0.1), 0 8px 16px -4px rgba(0, 0, 0, 0.05)',
  borderRadius: '16px', // 保持圆角不变
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    // 悬浮时阴影加深并扩散
    boxShadow: '0 30px 60px -8px rgba(37, 99, 235, 0.15), 0 12px 24px -6px rgba(0, 0, 0, 0.08)',
    // transform: 'translateY(-4px)', // 移除上浮效果
    borderColor: 'rgba(255, 255, 255, 0.9)'
  }
};

const HomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { user, loading, logout, refreshUser } = useAuth();
  const { checkPoints, showDialog, dialogProps, closeDialog } = usePointsCheck();

  // 标签页状态
  const [activeTab, setActiveTab] = useState<'veo2' | 'veo3' | 'veo3+' | 'sora-new'>('veo3+');
  // 模块选择状态
  const [currentModule, setCurrentModule] = useState<'veo' | 'sora' | 'grok' | 'seedance' | 'nano' | 'gpt'>('veo');
  // Grok 子标签页
  // grok-10s: wuyinkeji 旧通道, grok-30s: wuyinkeji Imagine 30s 优惠通道
  // grok-20s: newapi.medu.chat 特惠(20s 单段 / 60s 三段拼,同步 SSE)
  // grok-short: newapi.medu.chat 极速(6s/10s,走异步 /v1/videos,错误能透传上游原话)
  const [grokSubTab, setGrokSubTab] = useState<'grok-10s' | 'grok-30s' | 'grok-20s' | 'grok-short' | 'grok-stable'>('grok-stable');
  // Grok Imagine (30s) 时长
  const [grokImagineDuration, setGrokImagineDuration] = useState<number>(10);
  // Grok 稳定档(apimart 中转):6/12/20/30s
  const [grokStableDuration, setGrokStableDuration] = useState<number>(6);
  // Grok 20s 特惠:时长(20 单段 / 60 三段拼)+ 多张参考图(最多 7 张,跟 xAI Grok Imagine 上限一致)
  const [grokLiteDuration, setGrokLiteDuration] = useState<number>(20);
  const [grokLiteImages, setGrokLiteImages] = useState<string[]>([]);
  const [uploadingGrokLite, setUploadingGrokLite] = useState<boolean>(false);
  // 短 Grok 极速档(异步 /v1/videos):6s/10s 二选一,跟 20s 共享一份图片/比例
  const [grokShortDuration, setGrokShortDuration] = useState<6 | 10>(6);
  // 前端积分显示要跟后端 GROK_LITE_COST_BY_DURATION 一致(后端是单一真源)
  const GROK_LITE_COST_BY_DURATION: Record<number, number> = { 6: 6, 10: 10, 20: 20, 60: 60 };
  // Grok 稳定档积分要跟后端 GROK_STABLE_COST_BY_DURATION 一致(后端是单一真源)
  const GROK_STABLE_COST_BY_DURATION: Record<number, number> = { 6: 10, 12: 20, 20: 30, 30: 50 };
  const GROK_LITE_MAX_IMAGES = 7;

  // Seedance 2.0 Fast (x-see.cn 中转) — 5 种模式共用一套状态,提交时按 seedanceMode 取对应字段
  // 模式纯前端约定,API 本身无 mode 字段 —— 后端拼 content 数组时按 mode 走分支(详见 _build_seedance_content)
  const [seedanceMode, setSeedanceMode] = useState<'t2v' | 'i2v' | 'flf' | 'mmref' | 'edit'>('t2v');
  const [seedanceDuration, setSeedanceDuration] = useState<5 | 10 | 15>(5);
  const [seedanceFirstFrame, setSeedanceFirstFrame] = useState<string>('');
  const [seedanceLastFrame, setSeedanceLastFrame] = useState<string>('');
  const [seedanceRefImages, setSeedanceRefImages] = useState<string[]>([]);    // mmref / edit 都用
  const [seedanceVideoUrl, setSeedanceVideoUrl] = useState<string>('');         // mmref 可选 / edit 必填
  const [seedanceAudioUrl, setSeedanceAudioUrl] = useState<string>('');         // mmref / edit 可选
  const [seedanceGenerateAudio, setSeedanceGenerateAudio] = useState<boolean>(true);
  const [uploadingSeedanceFirst, setUploadingSeedanceFirst] = useState<boolean>(false);
  const [uploadingSeedanceLast, setUploadingSeedanceLast] = useState<boolean>(false);
  const [uploadingSeedanceRef, setUploadingSeedanceRef] = useState<boolean>(false);
  const [uploadingSeedanceVideo, setUploadingSeedanceVideo] = useState<boolean>(false);
  const [uploadingSeedanceAudio, setUploadingSeedanceAudio] = useState<boolean>(false);
  // 跟后端 SEEDANCE_COST_BY_DURATION 一致(后端是单一真源)
  const SEEDANCE_COST_BY_DURATION: Record<number, number> = { 5: 80, 10: 160, 15: 240 };
  const SEEDANCE_MAX_REFERENCE_IMAGES = 4;  // mmref 上限
  const SEEDANCE_EDIT_MAX_IMAGES = 3;       // edit 上限(给视频留主导)

  // 落地页卡片跳转：通过 ?m=gpt / ?m=grok30 预选模块
  useEffect(() => {
    const m = new URLSearchParams(location.search).get('m');
    if (!m) return;
    if (m === 'gpt') {
      setCurrentModule('gpt');
    } else if (m === 'grok30') {
      setCurrentModule('grok');
      setGrokSubTab('grok-30s');
      setGrokImagineDuration(30);
    } else if (m === 'nano') {
      setCurrentModule('nano');
    } else if (m === 'sora') {
      setCurrentModule('sora');
      setActiveTab('sora-new');
    } else if (m === 'seedance') {
      setCurrentModule('seedance');
    } else if (m === 'veo') {
      setCurrentModule('veo');
    }
    // 清除 query，避免刷新后一直生效
    navigate('/home', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 模型选择状态
  const [selectedModel, setSelectedModel] = useState<string>('veo_3_1');

  // Nano Banana states
  const [nanoModel, setNanoModel] = useState('nano-banana-2');
  const [nanoImageSize, setNanoImageSize] = useState('1K');
  const [nanoImages, setNanoImages] = useState<string[]>([]);

  // GPT Image states (wuyinkeji async API)
  const [gptSize, setGptSize] = useState<string>('auto');
  const [gptImages, setGptImages] = useState<string[]>([]);
  const [uploadingGpt, setUploadingGpt] = useState(false);

  // 表单状态
  const [prompt, setPrompt] = useState('');
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [duration, setDuration] = useState<number>(10);
  const [size, setSize] = useState<string>('small');
  const [image1, setImage1] = useState('');
  const [image2, setImage2] = useState('');
  const [image3, setImage3] = useState('');
  const [uploading1, setUploading1] = useState(false);
  const [uploading2, setUploading2] = useState(false);
  const [uploading3, setUploading3] = useState(false);
  const [uploadingNano, setUploadingNano] = useState(false);
  const [uploadingSora, setUploadingSora] = useState(false);
  const [uploadingGrok, setUploadingGrok] = useState(false);
  const [soraUrl, setSoraUrl] = useState(''); // Sora 专用 URL
  const [grokUrl, setGrokUrl] = useState(''); // Grok 10s 专用 URL（单图）
  const [grokImagineImages, setGrokImagineImages] = useState<string[]>([]); // Grok 30s 多参考图
  const [grokStableImages, setGrokStableImages] = useState<string[]>([]); // Grok 稳 多参考图(最多 7 张)
  // Sora2 文生视频表单状态（精简：仅保留必要字段）
  const [soraTextModel, setSoraTextModel] = useState<'sora-2'>('sora-2');
  const [isGenerating, setIsGenerating] = useState(false);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [generationResult, setGenerationResult] = useState<any>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState('');
  const pollTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pollingActiveRef = useRef<boolean>(false);
  const generatingModelRef = useRef<string>(''); // 用于记录当前正在生成的模型名称，以便在结果中展示
  // 防重复提交：最近一次请求的幂等键与时间戳
  const lastReqKeyRef = useRef<string>('');
  const lastReqAtRef = useRef<number>(0);

  // 页面持久化：加载本地缓存的结果与任务队列（避免切换或刷新后丢失）
  useEffect(() => {
    try {
      const cachedResult = localStorage.getItem('vwin_last_generation_result');
      const cachedJobs = localStorage.getItem('vwin_jobs');
      if (cachedResult) {
        setGenerationResult(JSON.parse(cachedResult));
      }
      if (cachedJobs) {
        const parsed = JSON.parse(cachedJobs);
        if (Array.isArray(parsed)) setJobs(parsed);
      }
    } catch (e) {
      console.warn('Load cache failed', e);
    }
  }, []);

  // 当结果变化时，写入本地缓存（避免依赖未声明的 jobs）
  useEffect(() => {
    try {
      if (generationResult) {
        localStorage.setItem('vwin_last_generation_result', JSON.stringify(generationResult));
      }
    } catch (e) {
      console.warn('Save cache failed', e);
    }
  }, [generationResult]);

  // 并行异步队列（用于 Veo / Sora；同时也承载 Nano 的历史与运行中占位）
  type Job = {
    id: string; // 本地队列ID
    taskId?: string; // 远端任务ID
    model: 'veo2' | 'veo2-fast-frames' | 'veo3' | 'veo3-fast' | 'veo3-frames' | 'veo3-pro' | 'veo3-pro-frames' | 'veo3.1-components' | 'veo3.1' | 'veo3.1-pro' | 'veo_3_1' | 'veo_3_1-fast' | 'veo_3_1-components-4K' | 'sora2-new' | 'grok-video-3-10s' | 'grok-imagine' | 'grok-imagine-lite' | 'grok-stable' | 'seedance-fast' | 'nano-banana' | 'nano-banana-2' | 'gpt-image-1';
    module?: 'veo' | 'sora' | 'grok' | 'seedance' | 'nano' | 'gpt'; // 所属模块
    prompt: string;
    aspect_ratio: string;
    images?: string[];
    // Sora 专用参数
    sora_url?: string;
    grok_url?: string;
    duration?: number;
    size?: string;
    // Seedance 专用:模式 + 首/尾帧 URL + 视频/音频 URL(mmref/edit 复用 images 当作图参考)
    seedance_mode?: 't2v' | 'i2v' | 'flf' | 'mmref' | 'edit';
    seedance_first_frame?: string;
    seedance_last_frame?: string;
    seedance_video_url?: string;
    seedance_audio_url?: string;
    seedance_generate_audio?: boolean;
    status: 'queued' | 'submitting' | 'running' | 'succeeded' | 'failed';
    createdAt: number;
    result?: { video_url?: string; image_url?: string; raw?: any };
    error?: string;
    progress?: number; // 0-100 进度百分比
  };

  const [jobs, setJobs] = useState<Job[]>([]);
  const [currentProgress, setCurrentProgress] = useState<number | null>(null); // 当前预览任务进度
  const MAX_PARALLEL = 3;
  // 后端已提供统一接口：生成 /api/generate/video，查询 /api/tasks/{task_id}
  // 服务端会根据 model 自动路由（veo2/veo3），前端无需区分

  // 仅在“正在发起请求”阶段锁定按钮；拿到 taskId 或加入队列后即解锁
  const isSubmitting = isGenerating && !taskId;

  // 当队列变化时，写入本地缓存
  useEffect(() => {
    try {
      localStorage.setItem('vwin_jobs', JSON.stringify(jobs));
    } catch (e) {
      console.warn('Save jobs cache failed', e);
    }
  }, [jobs]);

  // 拉取模型成本表，用于在生成按钮旁显示“生成（X积分）”
  const [modelCosts, setModelCosts] = useState<Record<string, Record<string, number>>>({});
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) return;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/model-costs`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) return;
        const data = await res.json();
        const map: Record<string, Record<string, number>> = {};
        for (const it of data) {
          const m = String(it.model);
          const r = String(it.role);
          const c = Number(it.cost) || 0;
          map[m] = map[m] || {};
          map[m][r] = c;
        }
        setModelCosts(map);
      } catch {}
    })();
  }, []);

  const currentRole = (user?.role || 'free') as string;
  const resolveCurrentModelForCost = () => {
    if (currentModule === 'veo') return selectedModel;
    if (currentModule === 'sora') return 'sora2-new';
    if (currentModule === 'grok') {
      if (grokSubTab === 'grok-30s') return 'grok-imagine';
      if (grokSubTab === 'grok-20s') return 'grok-imagine-lite';
      if (grokSubTab === 'grok-short') return 'grok-imagine-lite';
      return 'grok-video-3-10s';
    }
    if (currentModule === 'seedance') return 'seedance-fast';
    if (currentModule === 'nano') return nanoModel;
    if (currentModule === 'gpt') return 'gpt-image-1';
    return selectedModel;
  };
  const currentModelForCost = resolveCurrentModelForCost();
  // wuyinkeji 的两条 Grok 通道(grok-10s / grok-30s)上游维护期间禁止提交
  const wuyinkejiGrokBlocked = WUYINKEJI_GROK_DISABLED
    && currentModule === 'grok'
    && (grokSubTab === 'grok-10s' || grokSubTab === 'grok-30s');
  // grok-imagine 成本 = 用户选择的时长(秒);grok-imagine-lite 按 GROK_LITE_COST_BY_DURATION 表查(短档 15/18,长档 20/60)
  const currentCost = (currentModule === 'sora')
    ? 20  // sora-2-reverse 12s 一档,¥0.8684/请求 → 20 积分 (~130% 毛利)
    : (currentModule === 'seedance')
    ? (SEEDANCE_COST_BY_DURATION[seedanceDuration] ?? seedanceDuration * 4)
    : (currentModule === 'grok' && grokSubTab === 'grok-30s')
    ? grokImagineDuration
    : (currentModule === 'grok' && grokSubTab === 'grok-stable')
    ? (GROK_STABLE_COST_BY_DURATION[grokStableDuration] ?? grokStableDuration)
    : (currentModule === 'grok' && grokSubTab === 'grok-20s')
      ? (GROK_LITE_COST_BY_DURATION[grokLiteDuration] ?? grokLiteDuration)
      : (currentModule === 'grok' && grokSubTab === 'grok-short')
        ? (GROK_LITE_COST_BY_DURATION[grokShortDuration] ?? grokShortDuration)
        : (modelCosts[currentModelForCost]?.[currentRole] ?? 0);
  const hasCurrentCost = (currentModule === 'sora')
    ? true
    : (currentModule === 'seedance')
    ? true
    : (currentModule === 'grok' && (grokSubTab === 'grok-30s' || grokSubTab === 'grok-20s' || grokSubTab === 'grok-short' || grokSubTab === 'grok-stable'))
    ? true
    : (modelCosts[currentModelForCost] !== undefined && modelCosts[currentModelForCost][currentRole] !== undefined);

  // 轮询所有运行中的任务（每3秒）
  useEffect(() => {
    const interval = setInterval(async () => {
      // 支持 Veo/Sora 模块的队列轮询
      const running = jobs.filter(j => j.status === 'running' && j.taskId);
      if (running.length === 0) return;
      const token = localStorage.getItem('access_token');
      // 逐个查询状态
      const updates: Record<string, Partial<Job>> = {};
      for (const job of running) {
        try {
          let res: Response;
          if (job.module === 'sora') {
            // sora new 查询接口
            res = await fetch(`${API_BASE}/api/generate/video/sora2/result/${job.taskId}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
          } else if (job.module === 'grok') {
            let grokResultUrl: string;
            if (job.model === 'grok-imagine') {
              grokResultUrl = `${API_BASE}/api/generate/video/grok-imagine/result/${job.taskId}`;
            } else if (job.model === 'grok-imagine-lite') {
              grokResultUrl = `${API_BASE}/api/generate/video/grok-imagine-lite/result/${job.taskId}`;
            } else if (job.model === 'grok-stable') {
              grokResultUrl = `${API_BASE}/api/generate/video/grok-stable/result/${job.taskId}`;
            } else {
              grokResultUrl = `${API_BASE}/api/generate/video/grok/result/${job.taskId}`;
            }
            res = await fetch(grokResultUrl, {
              headers: { Authorization: `Bearer ${token}` }
            });
          } else if (job.module === 'seedance') {
            // Seedance 2.0 Fast 任务专用查询接口
            res = await fetch(`${API_BASE}/api/generate/video/seedance/result/${job.taskId}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
          } else if (job.model === 'veo_3_1' || job.model === 'veo_3_1-fast' || job.model === 'veo_3_1-components-4K') {
            // veo3+ 的三个 jeniya 中转模型：专用 jeniya-veo 查询接口
            res = await fetch(`${API_BASE}/api/generate/video/jeniya-veo/result/${job.taskId}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
          } else {
            // Veo 查询接口
            res = await fetch(`${API_BASE}/api/tasks/${job.taskId}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
          }
          if (!res.ok) continue;
          const data = await res.json();
          // 兼容更多状态：SUCCESS/success/succeeded/completed
          const statusRaw = (data.status || data.data?.status || '').toString().toLowerCase();

          // 解析进度字段：兼容 progress / process，可能是 '67%' 字符串或数值 67
          const pRaw = (
            data.progress !== undefined ? data.progress :
            data.data?.progress !== undefined ? data.data.progress :
            data.process !== undefined ? data.process :
            data.data?.process !== undefined ? data.data.process :
            undefined
          ) as any;
          let p: number | undefined;
          if (typeof pRaw === 'string') {
            const m = pRaw.match(/(\d{1,3})/);
            if (m) {
              const v = parseInt(m[1], 10);
              if (!isNaN(v)) p = Math.max(0, Math.min(100, v));
            }
          } else if (typeof pRaw === 'number') {
            p = Math.max(0, Math.min(100, Math.round(pRaw)));
          }

          if (['succeeded', 'success', 'completed'].includes(statusRaw)) {
            // 优先从 data.output 读取（字符串直链）
            const output = typeof data.data?.output === 'string' ? data.data.output : undefined;
            const videoUrl = output || data.video_url || data.data?.video_url || data.output?.video_url || data.result?.video_url;
            const imageUrl = data.url || data.data?.url || data.output?.url || data.result?.url;
            updates[job.id] = {
              status: 'succeeded',
              progress: 100,
              result: { video_url: videoUrl, image_url: imageUrl, raw: data }
            };
            // grok-imagine-lite 60s 部分成功:弹原因 + 退款提示
            if (job.model === 'grok-imagine-lite' && data.partial && data.warning) {
              setSnackbarMsg(String(data.warning).slice(0, 240));
              setSnackbarOpen(true);
            }
          } else if (['failed', 'error'].includes(statusRaw) || data.error) {
            const failText = data.error?.message || data.error || '未知错误';
            updates[job.id] = { status: 'failed', error: failText };
            // 弹出错误提醒
            setSnackbarMsg('生成失败: ' + failText);
            setSnackbarOpen(true);
          }
          // 若仍在运行或未终态，更新进度
          if (!['succeeded', 'success', 'completed'].includes(statusRaw) && !(['failed', 'error'].includes(statusRaw) || data.error)) {
            if (p !== undefined) {
              updates[job.id] = { ...(updates[job.id] || {}), progress: p };
            }
          }
        } catch (e: any) {
          console.error('poll error', e);
        }
      }
      if (Object.keys(updates).length) {
        setJobs(prev => prev.map(j => updates[j.id] ? { ...j, ...updates[j.id] } : j));
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [jobs, currentModule]);

  // 提交队列中的任务（最多并行3）
  useEffect(() => {
    const submitNext = async () => {
      // 支持当前模块的队列提交（veo/sora）
      const running = jobs.filter(j => j.status === 'running').length;
      const capacity = MAX_PARALLEL - running;
      if (capacity <= 0) return;
      const toSubmit = jobs.filter(j => j.status === 'queued').slice(0, capacity);
      if (toSubmit.length === 0) return;

      const token = localStorage.getItem('access_token');
      let needRefreshPoints = false;
      for (const job of toSubmit) {
        // 标记为提交中
        setJobs(prev => prev.map(j => j.id === job.id ? { ...j, status: 'submitting' } : j));
        try {
          let res: Response;
          if (job.module === 'sora') {
            // sora new 提交：App 端同款端点
            const payload: any = {
              prompt: job.prompt,
              duration: 12,
              aspect_ratio: job.aspect_ratio,
            };
            if (job.sora_url) payload.image_url = job.sora_url;
            res = await fetch(`${API_BASE}/api/generate/video/sora2`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify(payload)
            });
          } else if (job.module === 'grok') {
            if (job.model === 'grok-imagine') {
              res = await fetch(`${API_BASE}/api/generate/video/grok-imagine`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                  prompt: job.prompt,
                  duration: job.duration || 10,
                  aspect_ratio: job.aspect_ratio,
                  image_urls: (job.images && job.images.length)
                    ? job.images
                    : (job.grok_url ? [job.grok_url] : undefined),
                })
              });
            } else if (job.model === 'grok-imagine-lite') {
              res = await fetch(`${API_BASE}/api/generate/video/grok-imagine-lite`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                  prompt: job.prompt,
                  duration: job.duration || 20,
                  aspect_ratio: job.aspect_ratio,
                  image_urls: job.images && job.images.length ? job.images : undefined,
                })
              });
            } else if (job.model === 'grok-stable') {
              res = await fetch(`${API_BASE}/api/generate/video/grok-stable`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                  prompt: job.prompt,
                  duration: job.duration || 6,
                  aspect_ratio: job.aspect_ratio,
                  image_urls: job.images || [],
                })
              });
            } else {
              res = await fetch(`${API_BASE}/api/generate/video/grok`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                  model: 'grok-video-3-10s',
                  prompt: job.prompt,
                  url: job.grok_url,
                  aspectRatio: job.aspect_ratio,
                  size: job.size || '720P'
                })
              });
            }
          } else if (job.module === 'seedance') {
            // Seedance 2.0 Fast 提交:按 mode 拼 payload。
            // mmref/edit 共用 image_urls/video_url/audio_url 三个字段,后端按 mode 走 content 数组分支。
            const seedancePayload: any = {
              prompt: job.prompt,
              mode: job.seedance_mode || 't2v',
              duration: job.duration || 5,
              aspect_ratio: job.aspect_ratio,
              generate_audio: job.seedance_generate_audio !== false,
            };
            if (job.seedance_mode === 'i2v' && job.seedance_first_frame) {
              seedancePayload.first_frame_url = job.seedance_first_frame;
            } else if (job.seedance_mode === 'flf') {
              if (job.seedance_first_frame) seedancePayload.first_frame_url = job.seedance_first_frame;
              if (job.seedance_last_frame) seedancePayload.last_frame_url = job.seedance_last_frame;
            } else if (job.seedance_mode === 'mmref' || job.seedance_mode === 'edit') {
              if (job.images && job.images.length) seedancePayload.image_urls = job.images;
              if (job.seedance_video_url) seedancePayload.video_url = job.seedance_video_url;
              if (job.seedance_audio_url) seedancePayload.audio_url = job.seedance_audio_url;
            }
            res = await fetch(`${API_BASE}/api/generate/video/seedance`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify(seedancePayload)
            });
          } else if (job.model === 'veo_3_1' || job.model === 'veo_3_1-fast' || job.model === 'veo_3_1-components-4K') {
            // veo3+ 的三个 jeniya 中转模型：专用 jeniya-veo 入口
            res = await fetch(`${API_BASE}/api/generate/video/jeniya-veo`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                model: job.model,
                prompt: job.prompt,
                aspect_ratio: job.aspect_ratio,
                image_urls: job.images && job.images.length ? job.images : [],
              })
            });
          } else {
            const endpoint = '/api/generate/video';
            const payload: any = {
              prompt: job.prompt,
              model: job.model,
              aspect_ratio: job.aspect_ratio,
              enhance_prompt: true,
              enable_upsample: true,
              images: job.images && job.images.length ? job.images : undefined
            };
            res = await fetch(`${API_BASE}${endpoint}`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify(payload)
            });
          }
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            const detail = typeof err.detail === 'string'
              ? err.detail
              : (err.detail ? JSON.stringify(err.detail) : (err.message || '请求失败'));
            throw new Error(`[HTTP ${res.status}] ${detail}`);
          }
          const data = await res.json();
          const task_id = data.task_id;
          setJobs(prev => prev.map(j => j.id === job.id ? { ...j, status: 'running', taskId: task_id } : j));
          needRefreshPoints = true;
        } catch (e: any) {
          const msg = e?.message || '请求失败';
          setJobs(prev => prev.map(j => j.id === job.id ? { ...j, status: 'failed', error: msg } : j));
          setSnackbarMsg(`生成失败：${msg}`);
          setSnackbarOpen(true);
        }
      }

      // 扣分发生在“提交成功”时：提交完这一批后刷新一次用户积分
      if (needRefreshPoints) {
        try {
          await refreshUser();
        } catch {
          // ignore
        }
      }
    };
    submitNext();
  }, [jobs, currentModule, refreshUser]);
  
  // 调试：打印用户信息
  useEffect(() => {
    console.log('=== 用户信息调试 ===');
    console.log('Loading 状态:', loading);
    console.log('User object:', user);
    console.log('Username:', user?.username);
    console.log('Email:', user?.email);
    console.log('Token:', localStorage.getItem('access_token') ? '存在' : '不存在');
    console.log('==================');
  }, [user, loading]);
  
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    // 仅当没有 token 时跳转登录；有 token 即使暂时拉不到用户，也允许进入页面
    if (!token) {
      navigate('/login');
      return;
    }
    // 保留页面，等待 AuthProvider 完成鉴权；不要因 user 为空而重定向
  }, [navigate]);

  // 轮询任务状态
  useEffect(() => {
    // 安全兜底：一旦拿到 taskId，就立即释放按钮 loading，避免按钮被占用
    // 轮询继续在后台进行
    if (taskId) {
      setIsGenerating(false);
    }

    if (taskId) {
      const checkStatus = async () => {
        try {
          const token = localStorage.getItem('access_token');
          // 根据当前模块选择不同的查询接口
          // Nano 模块不需要轮询，直接返回结果，所以这里不需要处理 nano
          const statusUrl = currentModule === 'sora'
            ? `${API_BASE}/api/generate/video/sora2/result/${taskId}`
            : `${API_BASE}/api/tasks/${taskId}`;

          const res = await fetch(statusUrl, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            // 统一归一化状态大小写
            const statusRaw = (data.status || data.data?.status || '').toString().toLowerCase();
            // 解析当前任务进度（兼容 progress / process）
            const pRaw = (
              data.progress !== undefined ? data.progress :
              data.data?.progress !== undefined ? data.data.progress :
              data.process !== undefined ? data.process :
              data.data?.process !== undefined ? data.data.process :
              undefined
            ) as any;
            let p: number | null = null;
            if (typeof pRaw === 'string') {
              const m = pRaw.match(/(\d{1,3})/);
              if (m) {
                const v = parseInt(m[1], 10);
                if (!isNaN(v)) p = Math.max(0, Math.min(100, v));
              }
            } else if (typeof pRaw === 'number') {
              p = Math.max(0, Math.min(100, Math.round(pRaw)));
            }
            if (p !== null) setCurrentProgress(p);
            if (['succeeded', 'success', 'completed'].includes(statusRaw)) {
              // 立即停止当前轮询，避免在依赖变化清理前继续触发一次
              if (pollTimerRef.current) {
                clearInterval(pollTimerRef.current);
                pollTimerRef.current = null;
              }
              pollingActiveRef.current = false;
              // 注入模型名称
              const resultWithModel = { ...data, _model: generatingModelRef.current };
              setGenerationResult(resultWithModel);
              setIsGenerating(false);
              setTaskId(null);
              setCurrentProgress(null);
              setSnackbarMsg('视频生成成功！');
              setSnackbarOpen(true);
              // 任务终态后刷新一次积分（兼容后端可能的退款/补扣）
              try { await refreshUser(); } catch { /* ignore */ }
            } else if (['failed', 'failure', 'error'].includes(statusRaw) || data.error || data.fail_reason) {
              // 立即停止当前轮询
              if (pollTimerRef.current) {
                clearInterval(pollTimerRef.current);
                pollTimerRef.current = null;
              }
              pollingActiveRef.current = false;
              setIsGenerating(false);
              setTaskId(null);
              setCurrentProgress(null);
              const failMsg = (() => {
                if (typeof data.error === 'string') return data.error;
                if (data.error?.message) return data.error.message;
                if (typeof data.fail_reason === 'string' && data.fail_reason) {
                  try {
                    const fr = JSON.parse(data.fail_reason);
                    if (fr?.message) return fr.message;
                  } catch {}
                  return data.fail_reason;
                }
                return '未知错误';
              })();
              setSnackbarMsg('视频生成失败: ' + failMsg);
              setSnackbarOpen(true);
              // 失败时后端可能触发退款，终态后刷新积分
              try { await refreshUser(); } catch { /* ignore */ }
            }
            // 如果是 processing 或 pending，继续轮询
          }
        } catch (error) {
          console.error("Poll error", error);
        }
      };

      // 启动新轮询前，确保旧的已清理；避免重复定时器
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      if (!pollingActiveRef.current) {
        pollTimerRef.current = setInterval(checkStatus, 3000); // 每3秒轮询一次
        pollingActiveRef.current = true;
      }
    }

    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      pollingActiveRef.current = false;
      setCurrentProgress(null);
    };
  }, [taskId, currentModule, refreshUser]);

  // 切换标签页时重置模型
  useEffect(() => {
    if (currentModule !== 'veo') {
      return;
    }
    if (activeTab === 'veo2') {
      setSelectedModel('veo2');
    } else if (activeTab === 'veo3') {
      setSelectedModel('veo3');
    } else if (activeTab === 'veo3+') {
      setSelectedModel('veo_3_1');
    }
  }, [activeTab, currentModule]);

  // 当切换模型时，清空图片，避免误传
  useEffect(() => {
    setImage1('');
    setImage2('');
    setImage3('');
    // setNanoImages([]); // 切换模型时不清除 Nano 图片
  }, [selectedModel, currentModule]);

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    logout();
    navigate('/');
  };

  const handleMenu = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleGenerate = async () => {
    if (wuyinkejiGrokBlocked) {
      setSnackbarMsg('该通道正在维护，暂不可用，请改用其他档位或稍后再试');
      setSnackbarOpen(true);
      return;
    }
    if (!prompt.trim()) {
      setSnackbarMsg('请输入提示词');
      setSnackbarOpen(true);
      return;
    }

    // 参考图上传中防御：任一上传未完成则拒绝提交，避免把空 URL 发给上游
    const uploadInFlight = uploading1 || uploading2 || uploading3 || uploadingNano || uploadingSora || uploadingGrok || uploadingGpt;
    if (uploadInFlight) {
      setSnackbarMsg('参考图正在上传中，请等待上传完成后再生成');
      setSnackbarOpen(true);
      return;
    }

    // 积分检查：使用从后端获取的真实成本
    // 如果成本数据未加载完成，为了安全起见，阻止提交并提示用户
    let requiredPoints = 0;

    // 检查是否已成功获取到当前模型的成本数据
    if (!hasCurrentCost) {
      // 成本数据未加载，不允许提交
      setSnackbarMsg('正在加载模型成本信息，请稍候...');
      setSnackbarOpen(true);
      return;
    }

    // 使用从后端获取的真实成本
    requiredPoints = currentCost;

    // 额外的安全检查：如果成本为 0 或负数，说明数据异常
    if (requiredPoints <= 0) {
      console.error('[积分检查] 模型成本数据异常:', {
        model: currentModelForCost,
        role: currentRole,
        cost: requiredPoints,
        modelCosts
      });
      setSnackbarMsg('模型成本数据异常，请刷新页面重试');
      setSnackbarOpen(true);
      return;
    }

    // 执行积分检查
    if (!checkPoints(requiredPoints)) {
      return;
    }

    // 统一构造幂等键（按模块+模型+提示词+主要参数）
    const mkKey = (obj: any) => {
      try {
        const s = JSON.stringify(obj);
        let hash = 0;
        for (let i = 0; i < s.length; i++) hash = ((hash << 5) - hash) + s.charCodeAt(i);
        return `${obj.module || currentModule}:${obj.model || selectedModel}:${hash}`;
      } catch { return `${currentModule}:${selectedModel}:${prompt.slice(0,32)}`; }
    };
    // Veo 模块：改为异步队列（允许多次提交，并行上限3）
    // 支持 veo2 与 veo3 / veo3+ 系列（统一走后端 /api/generate/video；后端按 model 路由）
    const queueModels = [
      'veo2', 'veo2-fast-frames',
      'veo3', 'veo3-fast', 'veo3-frames',
      'veo3-pro', 'veo3-pro-frames',
      'veo3.1-components', 'veo3.1', 'veo3.1-pro'
    ];
    // veo3+ 的三个 jeniya 中转模型走专用 jeniya-veo 入口（异步队列）
    const jeniyaVeoModels = ['veo_3_1', 'veo_3_1-fast', 'veo_3_1-components-4K'];
    if (currentModule === 'veo' && jeniyaVeoModels.includes(selectedModel)) {
      const images: string[] = [];
      if (image1) images.push(image1);
      if (image2) images.push(image2);
      if (image3) images.push(image3);
      const reqKey = mkKey({ module: 'veo', model: selectedModel, prompt, aspectRatio, images });
      const now = Date.now();
      if (lastReqKeyRef.current === reqKey && (now - lastReqAtRef.current) < 5000) {
        setSnackbarMsg('短时间内重复提交，已忽略');
        setSnackbarOpen(true);
        return;
      }
      lastReqKeyRef.current = reqKey; lastReqAtRef.current = now;
      const id = Math.random().toString(36).slice(2);
      const newJob: Job = {
        id,
        module: 'veo',
        model: selectedModel as Job['model'],
        prompt,
        aspect_ratio: aspectRatio,
        images: images.length ? images : undefined,
        status: 'queued',
        progress: 0,
        createdAt: Date.now()
      };
      setJobs(prev => [newJob, ...prev]);
      setSnackbarMsg('任务已加入队列');
      setSnackbarOpen(true);
      setIsGenerating(false);
      return;
    }

    if (currentModule === 'veo' && queueModels.includes(selectedModel)) {
      const images: string[] = [];
      if (image1) images.push(image1);
      if (image2) images.push(image2);
      if (image3) images.push(image3);
      const reqKey = mkKey({ module: 'veo', model: selectedModel, prompt, aspectRatio, images });
      const now = Date.now();
      if (lastReqKeyRef.current === reqKey && (now - lastReqAtRef.current) < 5000) {
        setSnackbarMsg('短时间内重复提交，已忽略');
        setSnackbarOpen(true);
        return;
      }
      lastReqKeyRef.current = reqKey; lastReqAtRef.current = now;
      const id = Math.random().toString(36).slice(2);
      const newJob: Job = {
        id,
        module: 'veo',
        model: selectedModel as Job['model'],
        prompt,
        aspect_ratio: aspectRatio,
        images: images.length ? images : undefined,
        status: 'queued',
        progress: 0,
        createdAt: Date.now()
      };
      setJobs(prev => [newJob, ...prev]);
      setSnackbarMsg('任务已加入队列');
  setSnackbarOpen(true);
  // 队列提交成功后，不阻塞按钮，允许继续发起其他请求
      setIsGenerating(false);
      return;
    }

    if (currentModule === 'grok') {
      if (grokSubTab === 'grok-stable') {
        // grok-stable 稳定档(apimart 中转):支持最多 7 张参考图
        const reqKey = mkKey({ module: 'grok', model: 'grok-stable', prompt, aspectRatio, duration: grokStableDuration, images: grokStableImages });
        const now = Date.now();
        if (lastReqKeyRef.current === reqKey && (now - lastReqAtRef.current) < 5000) {
          setSnackbarMsg('短时间内重复提交，已忽略');
          setSnackbarOpen(true);
          return;
        }
        lastReqKeyRef.current = reqKey; lastReqAtRef.current = now;
        const id = Math.random().toString(36).slice(2);
        const newJob: Job = {
          id,
          module: 'grok',
          model: 'grok-stable',
          prompt,
          aspect_ratio: aspectRatio,
          images: grokStableImages.length ? [...grokStableImages] : undefined,
          duration: grokStableDuration,
          status: 'queued',
          progress: 0,
          createdAt: Date.now()
        };
        setJobs(prev => [newJob, ...prev]);
        setSnackbarMsg('稳 Grok 任务已加入队列');
        setSnackbarOpen(true);
        setIsGenerating(false);
        return;
      }

      if (grokSubTab === 'grok-30s') {
        // grok-imagine (30s) 任务：支持多参考图
        const reqKey = mkKey({ module: 'grok', model: 'grok-imagine', prompt, aspectRatio, duration: grokImagineDuration, images: grokImagineImages });
        const now = Date.now();
        if (lastReqKeyRef.current === reqKey && (now - lastReqAtRef.current) < 5000) {
          setSnackbarMsg('短时间内重复提交，已忽略');
          setSnackbarOpen(true);
          return;
        }
        lastReqKeyRef.current = reqKey; lastReqAtRef.current = now;
        const id = Math.random().toString(36).slice(2);
        const newJob: Job = {
          id,
          module: 'grok',
          model: 'grok-imagine',
          prompt,
          aspect_ratio: aspectRatio,
          images: grokImagineImages.length ? [...grokImagineImages] : undefined,
          duration: grokImagineDuration,
          status: 'queued',
          progress: 0,
          createdAt: Date.now()
        };
        setJobs(prev => [newJob, ...prev]);
        setSnackbarMsg('30s Grok 任务已加入队列');
        setSnackbarOpen(true);
        setIsGenerating(false);
        return;
      }

      if (grokSubTab === 'grok-20s' || grokSubTab === 'grok-short') {
        // grok-imagine-lite 走同一后端入口:
        //   grok-20s 档: 20/60s,同步 SSE
        //   grok-short 档: 6/10s,异步 /v1/videos(失败原话能透传)
        const liteRatio = aspectRatio === '16:9' ? '16:9' : '9:16';
        const dur = grokSubTab === 'grok-short'
          ? grokShortDuration
          : (grokLiteDuration === 60 ? 60 : 20);
        const reqKey = mkKey({ module: 'grok', model: 'grok-imagine-lite', prompt, aspectRatio: liteRatio, duration: dur, images: grokLiteImages });
        const now = Date.now();
        if (lastReqKeyRef.current === reqKey && (now - lastReqAtRef.current) < 5000) {
          setSnackbarMsg('短时间内重复提交，已忽略');
          setSnackbarOpen(true);
          return;
        }
        lastReqKeyRef.current = reqKey; lastReqAtRef.current = now;
        const id = Math.random().toString(36).slice(2);
        const newJob: Job = {
          id,
          module: 'grok',
          model: 'grok-imagine-lite',
          prompt,
          aspect_ratio: liteRatio,
          images: grokLiteImages.length ? [...grokLiteImages] : undefined,
          duration: dur,
          status: 'queued',
          progress: 0,
          createdAt: Date.now()
        };
        setJobs(prev => [newJob, ...prev]);
        const isShort = grokSubTab === 'grok-short';
        setSnackbarMsg(
          isShort
            ? `${dur}s 短 Grok 任务已提交(极速通道,通常 1 分钟内出片)`
            : `${dur}s Grok 特惠任务已加入队列${dur === 60 ? '(预计 12 分钟,可继续浏览)' : ''}`
        );
        setSnackbarOpen(true);
        setIsGenerating(false);
        return;
      }

      // grok-video-3-10s 任务（原逻辑）
      const reqKey = mkKey({ module: 'grok', model: 'grok-video-3-10s', prompt, aspectRatio, url: grokUrl });
      const now = Date.now();
      if (lastReqKeyRef.current === reqKey && (now - lastReqAtRef.current) < 5000) {
        setSnackbarMsg('短时间内重复提交，已忽略');
        setSnackbarOpen(true);
        return;
      }
      lastReqKeyRef.current = reqKey; lastReqAtRef.current = now;
      const id = Math.random().toString(36).slice(2);
      const newJob: Job = {
        id,
        module: 'grok',
        model: 'grok-video-3-10s',
        prompt,
        aspect_ratio: aspectRatio,
        grok_url: grokUrl,
        size: '720P',
        status: 'queued',
        progress: 0,
        createdAt: Date.now()
      };
      setJobs(prev => [newJob, ...prev]);
      setSnackbarMsg('Grok 任务已加入队列');
      setSnackbarOpen(true);
      setIsGenerating(false);
      return;
    }

    if (currentModule === 'seedance') {
      // Seedance 2.0 Fast — 按模式校验输入,然后入队
      if (seedanceMode === 'i2v' && !seedanceFirstFrame) {
        setSnackbarMsg('图生视频模式需要先上传一张首帧图');
        setSnackbarOpen(true);
        setIsGenerating(false);
        return;
      }
      if (seedanceMode === 'flf' && (!seedanceFirstFrame || !seedanceLastFrame)) {
        setSnackbarMsg('首尾帧模式需要上传首帧和尾帧两张图');
        setSnackbarOpen(true);
        setIsGenerating(false);
        return;
      }
      if (seedanceMode === 'mmref' && seedanceRefImages.length === 0 && !seedanceVideoUrl && !seedanceAudioUrl) {
        setSnackbarMsg('多模态参考模式至少需要 1 张图 / 1 段视频 / 1 段音频');
        setSnackbarOpen(true);
        setIsGenerating(false);
        return;
      }
      if (seedanceMode === 'edit' && !seedanceVideoUrl) {
        setSnackbarMsg('编辑视频模式必须先上传一段待编辑的视频');
        setSnackbarOpen(true);
        setIsGenerating(false);
        return;
      }

      const reqKey = mkKey({
        module: 'seedance', model: 'seedance-fast', prompt, aspectRatio,
        mode: seedanceMode, duration: seedanceDuration,
        first: seedanceFirstFrame, last: seedanceLastFrame, refs: seedanceRefImages,
        video: seedanceVideoUrl, audio: seedanceAudioUrl,
      });
      const now = Date.now();
      if (lastReqKeyRef.current === reqKey && (now - lastReqAtRef.current) < 5000) {
        setSnackbarMsg('短时间内重复提交，已忽略');
        setSnackbarOpen(true);
        setIsGenerating(false);
        return;
      }
      lastReqKeyRef.current = reqKey; lastReqAtRef.current = now;

      const needsImages = seedanceMode === 'mmref' || seedanceMode === 'edit';
      const id = Math.random().toString(36).slice(2);
      const newJob: Job = {
        id,
        module: 'seedance',
        model: 'seedance-fast',
        prompt,
        aspect_ratio: aspectRatio,
        duration: seedanceDuration,
        seedance_mode: seedanceMode,
        seedance_first_frame: seedanceFirstFrame || undefined,
        seedance_last_frame: seedanceLastFrame || undefined,
        seedance_video_url: needsImages && seedanceVideoUrl ? seedanceVideoUrl : undefined,
        seedance_audio_url: needsImages && seedanceAudioUrl ? seedanceAudioUrl : undefined,
        images: needsImages && seedanceRefImages.length ? [...seedanceRefImages] : undefined,
        seedance_generate_audio: seedanceGenerateAudio,
        status: 'queued',
        progress: 0,
        createdAt: Date.now()
      };
      setJobs(prev => [newJob, ...prev]);
      setSnackbarMsg(seedanceMode === 'edit' ? 'Seedance 编辑任务已加入队列' : 'Seedance 任务已加入队列');
      setSnackbarOpen(true);
      setIsGenerating(false);
      return;
    }

    // 其他模块保持原逻辑
    // 若已在生成，避免再次触发
    if (isGenerating) {
      setSnackbarMsg('正在生成中，请稍候…');
      setSnackbarOpen(true);
      return;
    }
    setIsGenerating(true);
    setGenerationResult(null);
    setTaskId(null);

    const token = localStorage.getItem('access_token');
    let endpoint = '';
    let payload: any = {};

    // 记录当前模型名称
    const currentModelName = currentModule === 'sora'
      ? 'sora new'
      : currentModule === 'nano'
        ? nanoModel
        : currentModule === 'gpt'
          ? 'gpt-image-1'
          : selectedModel;
    generatingModelRef.current = currentModelName;

    if (currentModule === 'sora') {
      // sora new：走异步队列，后端 /api/generate/video/sora2（12s 固定）
      const id = Math.random().toString(36).slice(2);
      const reqKey = mkKey({ module: 'sora', model: 'sora2-new', prompt, aspectRatio, url: soraUrl });
      const now = Date.now();
      if (lastReqKeyRef.current === reqKey && (now - lastReqAtRef.current) < 5000) {
        setSnackbarMsg('短时间内重复提交，已忽略');
        setSnackbarOpen(true);
        setIsGenerating(false);
        return;
      }
      lastReqKeyRef.current = reqKey; lastReqAtRef.current = now;
      const newJob: Job = {
        id,
        module: 'sora',
        model: 'sora2-new' as any,
        prompt,
        aspect_ratio: aspectRatio,
        sora_url: soraUrl || undefined,
        duration: 12,
        status: 'queued',
        progress: 0,
        createdAt: Date.now()
      };
      setJobs(prev => [newJob, ...prev]);
      setSnackbarMsg('任务已加入队列');
      setSnackbarOpen(true);
      setIsGenerating(false);
      return;
    } else if (currentModule === 'nano') {
      endpoint = '/api/proxy/nano/generate';
      payload = {
        model: nanoModel,
        prompt,
        aspect_ratio: aspectRatio,
        image_size: nanoModel === 'nano-banana-2' ? nanoImageSize : undefined,
        images: nanoImages.length > 0 ? nanoImages : undefined
      };
      // Nano 直接返回结果，也做幂等检查
      const reqKey = mkKey({ module: 'nano', model: nanoModel, prompt, aspectRatio, image_size: nanoImageSize, images: nanoImages });
      const now = Date.now();
      if (lastReqKeyRef.current === reqKey && (now - lastReqAtRef.current) < 5000) {
        setSnackbarMsg('短时间内重复提交，已忽略');
        setSnackbarOpen(true);
        setIsGenerating(false);
        return;
      }
      lastReqKeyRef.current = reqKey; lastReqAtRef.current = now;
      // 为 Nano 也插入一个“运行中”占位任务到历史列表中，便于在任何模块下可见
      const nanoPendingId = Math.random().toString(36).slice(2);
      const nanoPendingJob: Job = {
        id: nanoPendingId,
        module: 'nano',
        model: nanoModel as Job['model'],
        prompt,
        aspect_ratio: aspectRatio,
        images: nanoImages.length ? nanoImages : undefined,
        status: 'running',
        progress: 0,
        createdAt: Date.now()
      };
      setJobs(prev => [nanoPendingJob, ...prev]);
      // 把占位ID临时挂到 payload 上，供后续成功/失败时更新（局部变量在 try/catch 中也可访问）
      (payload as any)._nanoPendingId = nanoPendingId;
    } else if (currentModule === 'gpt') {
      endpoint = '/api/proxy/gpt-image/generate';
      payload = {
        prompt,
        size: gptSize,
        images: gptImages.length > 0 ? gptImages : undefined
      };
      // GPT 与 Nano 同步返回，做幂等检查
      const reqKey = mkKey({ module: 'gpt', model: 'gpt-image-1', prompt, size: gptSize, images: gptImages });
      const now = Date.now();
      if (lastReqKeyRef.current === reqKey && (now - lastReqAtRef.current) < 5000) {
        setSnackbarMsg('短时间内重复提交，已忽略');
        setSnackbarOpen(true);
        setIsGenerating(false);
        return;
      }
      lastReqKeyRef.current = reqKey; lastReqAtRef.current = now;
      // 同样插入“运行中”占位任务
      const gptPendingId = Math.random().toString(36).slice(2);
      const gptPendingJob: Job = {
        id: gptPendingId,
        module: 'gpt',
        model: 'gpt-image-1' as Job['model'],
        prompt,
        aspect_ratio: aspectRatio,
        images: gptImages.length ? gptImages : undefined,
        status: 'running',
        progress: 0,
        createdAt: Date.now()
      };
      setJobs(prev => [gptPendingJob, ...prev]);
      (payload as any)._nanoPendingId = gptPendingId;
    } else {
      endpoint = '/api/generate/video';

      const images = [];
      if (image1) images.push(image1);
      if (image2) images.push(image2);
      if (image3) images.push(image3);

      payload = {
        prompt,
        model: selectedModel,
        aspect_ratio: aspectRatio,
        enhance_prompt: true,
        enable_upsample: true,
        images: images.length > 0 ? images : undefined
      };
      const reqKey = mkKey({ module: 'veo', model: selectedModel, prompt, aspectRatio, images });
      const now = Date.now();
      if (lastReqKeyRef.current === reqKey && (now - lastReqAtRef.current) < 5000) {
        setSnackbarMsg('短时间内重复提交，已忽略');
        setSnackbarOpen(true);
        setIsGenerating(false);
        return;
      }
      lastReqKeyRef.current = reqKey; lastReqAtRef.current = now;
    }

    try {
      const res = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || '请求失败');
      }

      const data = await res.json();

      if (currentModule === 'nano' || currentModule === 'gpt') {
        // 提交成功即扣分：刷新积分，让 UI 立刻变化
        try { await refreshUser(); } catch { /* ignore */ }
        // Nano/GPT 直接返回结果，同时把历史“运行中”占位替换为成功卡片（可能返回多张）
        const resultWithModel = { ...data, _model: currentModelName } as any;
        const pendingId = (payload as any)._nanoPendingId as string | undefined;
        const successModule: Job['module'] = currentModule === 'gpt' ? 'gpt' : 'nano';
        const successModel: Job['model'] = currentModule === 'gpt' ? ('gpt-image-1' as Job['model']) : (nanoModel as Job['model']);
        // 规范化为数组 URL 列表
        let urls: string[] = [];
        if (Array.isArray(resultWithModel?.data)) {
          urls = resultWithModel.data.map((it: any) => it?.url || it?.image_url || it).filter(Boolean);
        } else {
          const u = resultWithModel?.data?.url || resultWithModel?.image_url || resultWithModel?.url;
          if (u) urls = [u];
        }
        // 去重
        const seen = new Set<string>();
        urls = urls.filter(u => { const k = String(u); if (seen.has(k)) return false; seen.add(k); return true; });

        setJobs(prev => {
          // 删除占位卡
          const withoutPending = pendingId ? prev.filter(j => j.id !== pendingId) : prev.slice();
          // 追加成功卡（多图则拆分为多条）
          const successJobs: Job[] = urls.length ? urls.map(u => ({
            id: Math.random().toString(36).slice(2),
            module: successModule,
            model: successModel,
            prompt,
            aspect_ratio: aspectRatio,
            status: 'succeeded',
            createdAt: Date.now(),
            result: { image_url: u, raw: resultWithModel }
          })) : pendingId ? [{
            id: pendingId,
            module: successModule,
            model: successModel,
            prompt,
            aspect_ratio: aspectRatio,
            status: 'succeeded',
            createdAt: Date.now(),
            result: { raw: resultWithModel }
          } as Job] : [];
          return [...successJobs, ...withoutPending];
        });

        setGenerationResult(resultWithModel);
        setIsGenerating(false);
        setSnackbarMsg('图片生成成功！');
        setSnackbarOpen(true);
      } else {
  setTaskId(data.task_id);
  setCurrentProgress(0);
  // 已拿到任务ID，改为异步轮询，不再占用按钮的“生成中”状态
  setIsGenerating(false);
        // 提交成功即扣分：刷新积分
        try { await refreshUser(); } catch { /* ignore */ }
        setSnackbarMsg('任务已提交，正在生成中...');
        setSnackbarOpen(true);
      }

    } catch (error: any) {
      setIsGenerating(false);
      // 统一对用户的异常说明（含 Nano/GPT 接口异常的文案）
      const msg = (currentModule === 'nano')
        ? `Nano API 请求异常：${error?.message || '请稍后重试'}`
        : (currentModule === 'gpt')
          ? `GPT Image API 请求异常：${error?.message || '请稍后重试'}`
          : (error?.message || '请求失败');
      setSnackbarMsg(msg);
      setSnackbarOpen(true);
      // Nano/GPT 失败：直接移除"运行中"占位卡片，不在历史区保留失败卡；同时无需轮询
      if (currentModule === 'nano' || currentModule === 'gpt') {
        const pendingId = (payload as any)?._nanoPendingId as string | undefined;
        if (pendingId) {
          setJobs(prev => prev.filter(j => j.id !== pendingId));
        }
        // 失败时后端会自动退还预扣积分，刷新积分让 UI 立即同步
        try { await refreshUser(); } catch { /* ignore */ }
      }
    } finally {
      // 双保险：任何情况下都解除按钮占用；对于需要轮询的任务，taskId 控制显示卡片即可
      setIsGenerating(false);
    }
  };

  // 上传到后端让其转存至 OSS
  const uploadReferenceImage = async (file: File): Promise<string> => {
    const token = localStorage.getItem('access_token');
    if (!token) throw new Error('请先登录');

    const formData = new FormData();
    formData.append('file', file);

    const res = await fetch(`${API_BASE}/api/upload/image`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    const bodyText = await res.text();

    if (!res.ok) {
      let detail = '';
      try {
        const parsed = JSON.parse(bodyText);
        detail = parsed?.detail || '';
      } catch (e) {
        detail = bodyText;
      }
      throw new Error(`上传失败：${detail || res.statusText}`);
    }

    try {
      const data = JSON.parse(bodyText);
      if (!data?.url) throw new Error('上传失败：未返回地址');
      return data.url as string;
    } catch (e) {
      throw new Error('上传失败：返回格式错误');
    }
  };

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <Box minHeight="100vh" display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={2} sx={{ bgcolor: 'background.default' }}>
          <CircularProgress color="primary" />
          <Typography variant="body2" color="text.secondary">正在加载数据...</Typography>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{
        minHeight: '100vh',
        // 弥散光感背景 - 调优版：更清爽、更白净
        bgcolor: '#f8fafc',
        background: `
          radial-gradient(circle at 15% 15%, rgba(219, 234, 254, 0.4) 0%, transparent 50%),
          radial-gradient(circle at 85% 30%, rgba(255, 255, 255, 0.8) 0%, transparent 50%), /* 右侧调白 */
          radial-gradient(circle at 50% 0%, rgba(239, 246, 255, 0.5) 0%, transparent 60%),
          linear-gradient(to bottom, #f0f9ff 0%, #f8fafc 100%)
        `,
        // 叠加微妙噪点纹理增加质感
        backgroundImage: `
          radial-gradient(circle at 15% 15%, rgba(219, 234, 254, 0.4) 0%, transparent 50%),
          radial-gradient(circle at 85% 30%, rgba(255, 255, 255, 0.8) 0%, transparent 50%), /* 右侧调白 */
          radial-gradient(circle at 50% 0%, rgba(239, 246, 255, 0.5) 0%, transparent 60%),
          linear-gradient(to bottom, #f0f9ff 0%, #f8fafc 100%),
          url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.03'/%3E%3C/svg%3E")
        `,
        backgroundSize: '100% 100%, 100% 100%, 100% 100%, 100% 100%, cover',
        position: 'relative',
        pb: 4,
        // 添加微妙的内阴影,增强深度
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '300px',
          background: 'linear-gradient(to bottom, rgba(239, 246, 255, 0.3) 0%, transparent 100%)',
          pointerEvents: 'none',
          zIndex: 0
        }
      }}>
        {/* 1. 顶部导航栏 */}
      <TopNavBar />
      {/* 已统一 TopNavBar，旧 AppBar 代码移除 */}

        <Box sx={{
          mt: 4,
          width: { xs: '95%', sm: '92%', md: '90%', lg: '88%', xl: '85%' },
          maxWidth: '1920px',
          mx: 'auto',
          px: { xs: 1, sm: 2, md: 3 },
          position: 'relative',
          zIndex: 1
        }}>
          <ThreeDCarousel />

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '280px 1fr' }, gap: 3, alignItems: 'stretch' }}>
            {/* 左侧：模型选择栏 */}
            <Paper elevation={0} sx={{ ...cardBase, p: 0, display: 'flex', flexDirection: 'column', minHeight: 560 }}>
              <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0' }}>
                <Typography variant="subtitle1" fontWeight={700}>模型选择</Typography>
                <Typography variant="caption" color="text.secondary">选择视频生成模型</Typography>
              </Box>
              <Box sx={{ flex: 1, overflowY: 'auto' }}>
                <List component="nav" sx={{ py: 1 }}>
                  <ListItemButton
                    selected={currentModule === 'veo'}
                    onClick={() => {
                      setCurrentModule('veo');
                      setActiveTab('veo3+');
                      setAspectRatio('16:9');
                    }}
                    sx={{
                      mb: 1, mx: 1, borderRadius: 1,
                      bgcolor: currentModule === 'veo' ? 'rgba(37, 99, 235, 0.08)' : 'transparent',
                      color: currentModule === 'veo' ? 'primary.main' : 'inherit',
                      '&.Mui-selected': { 
                        bgcolor: 'rgba(37, 99, 235, 0.12)',
                        '&:hover': { bgcolor: 'rgba(37, 99, 235, 0.12)' }
                      }
                    }}
                  >
                    <ListItemIcon sx={{ color: currentModule === 'veo' ? '#4285F4' : '#64748b', minWidth: 40 }}>
                      <SiGoogle size={22} />
                    </ListItemIcon>
                    <ListItemText primary="Veo" secondary="适合通用视频生成" />
                  </ListItemButton>

                  <ListItemButton
                    selected={currentModule === 'sora'}
                    onClick={() => {
                      setCurrentModule('sora');
                      setActiveTab('sora-new');
                      setAspectRatio('16:9');
                    }}
                    sx={{
                      mb: 1, mx: 1, borderRadius: 1,
                      bgcolor: currentModule === 'sora' ? 'rgba(16, 185, 129, 0.08)' : 'transparent',
                      color: currentModule === 'sora' ? 'secondary.main' : 'inherit',
                      '&.Mui-selected': { 
                        bgcolor: 'rgba(16, 185, 129, 0.12)',
                        '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.12)' }
                      }
                    }}
                  >
                    <ListItemIcon sx={{ color: currentModule === 'sora' ? '#10A37F' : '#64748b', minWidth: 40 }}>
                      <SiOpenai size={22} />
                    </ListItemIcon>
                    <ListItemText primary="Sora" secondary="Sora2 视频生成" />
                  </ListItemButton>

                  <ListItemButton
                    selected={currentModule === 'grok'}
                    onClick={() => {
                      setCurrentModule('grok');
                      setAspectRatio('1:1');
                    }}
                    sx={{
                      mb: 1, mx: 1, borderRadius: 1,
                      bgcolor: currentModule === 'grok' ? 'rgba(236, 72, 153, 0.08)' : 'transparent',
                      color: currentModule === 'grok' ? '#db2777' : 'inherit',
                      '&.Mui-selected': {
                        bgcolor: 'rgba(236, 72, 153, 0.12)',
                        '&:hover': { bgcolor: 'rgba(236, 72, 153, 0.12)' }
                      }
                    }}
                  >
                    <ListItemIcon sx={{ color: currentModule === 'grok' ? '#000000' : '#64748b', minWidth: 40 }}>
                      <SiX size={22} />
                    </ListItemIcon>
                    <ListItemText primary="Grok" secondary="AI 图生视频" />
                  </ListItemButton>

                  <ListItemButton
                    selected={currentModule === 'seedance'}
                    onClick={() => {
                      setCurrentModule('seedance');
                      setAspectRatio('16:9');
                    }}
                    sx={{
                      mb: 1, mx: 1, borderRadius: 1,
                      bgcolor: currentModule === 'seedance' ? 'rgba(139, 92, 246, 0.08)' : 'transparent',
                      color: currentModule === 'seedance' ? '#7c3aed' : 'inherit',
                      '&.Mui-selected': {
                        bgcolor: 'rgba(139, 92, 246, 0.12)',
                        '&:hover': { bgcolor: 'rgba(139, 92, 246, 0.12)' }
                      }
                    }}
                  >
                    <ListItemIcon sx={{ color: currentModule === 'seedance' ? '#000000' : '#64748b', minWidth: 40 }}>
                      <SiBytedance size={22} />
                    </ListItemIcon>
                    <ListItemText primary="Seedance" secondary="Seedance 2.0 视频生成" />
                  </ListItemButton>

                  <ListItemButton
                    selected={currentModule === 'nano'}
                    onClick={() => {
                      setCurrentModule('nano');
                      setAspectRatio('16:9');
                    }}
                    sx={{
                      mb: 1, mx: 1, borderRadius: 1,
                      bgcolor: currentModule === 'nano' ? 'rgba(245, 158, 11, 0.08)' : 'transparent',
                      color: currentModule === 'nano' ? '#f59e0b' : 'inherit',
                      '&.Mui-selected': { 
                        bgcolor: 'rgba(245, 158, 11, 0.12)',
                        '&:hover': { bgcolor: 'rgba(245, 158, 11, 0.12)' }
                      }
                    }}
                  >
                    <ListItemIcon sx={{ color: currentModule === 'nano' ? '#4796E3' : '#64748b', minWidth: 40 }}>
                      <SiGooglegemini size={22} />
                    </ListItemIcon>
                    <ListItemText primary="Nano Banana" secondary="AI 图片生成" />
                  </ListItemButton>

                  <ListItemButton
                    selected={currentModule === 'gpt'}
                    onClick={() => {
                      setCurrentModule('gpt');
                      setAspectRatio('1:1');
                    }}
                    sx={{
                      mb: 1, mx: 1, borderRadius: 1,
                      bgcolor: currentModule === 'gpt' ? 'rgba(99, 102, 241, 0.08)' : 'transparent',
                      color: currentModule === 'gpt' ? '#6366f1' : 'inherit',
                      '&.Mui-selected': {
                        bgcolor: 'rgba(99, 102, 241, 0.12)',
                        '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.12)' }
                      }
                    }}
                  >
                    <ListItemIcon sx={{ color: currentModule === 'gpt' ? '#10A37F' : '#64748b', minWidth: 40 }}>
                      <SiOpenai size={22} />
                    </ListItemIcon>
                    <ListItemText primary="GPT Image" secondary="GPT-Image-2 图片生成" />
                  </ListItemButton>
                </List>
              </Box>
            </Paper>

            {/* 右侧：工作区（标题与分隔线全宽，结果区域从线下方开始） */}
            <Paper elevation={0} sx={{ ...cardBase, minHeight: 560, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                {currentModule === 'veo' ? (
                  <>
                    {/* Veo3+ Tab */}
                    <Box
                      onClick={() => setActiveTab('veo3+')}
                      sx={{
                        position: 'relative',
                        bgcolor: activeTab === 'veo3+' ? '#10b981' : '#e2e8f0',
                        color: activeTab === 'veo3+' ? 'white' : '#64748b',
                        pl: 3,
                        pr: 3,
                        py: 0.75,
                        width: 'fit-content',
                        cursor: 'pointer',
                        clipPath: 'polygon(0% 0%, calc(100% - 12px) 0%, 100% 50%, calc(100% - 12px) 100%, 0% 100%, 12px 50%)',
                        display: 'flex',
                        alignItems: 'center',
                        overflow: 'hidden',
                        transition: 'all 0.3s',
                        '&::after': activeTab === 'veo3+' ? {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          bottom: 0,
                          width: '6px',
                          bgcolor: 'rgba(255, 255, 255, 0.4)',
                          transform: 'skewX(-20deg)',
                          animation: `${shine} 1.5s infinite linear`
                        } : {}
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight={700}>
                        veo3+
                      </Typography>
                    </Box>
                  </>
                ) : currentModule === 'sora' ? (
                  /* Sora Tab */
                  <>
                    <Box
                      onClick={() => setActiveTab('sora-new')}
                      sx={{
                        position: 'relative',
                        bgcolor: activeTab === 'sora-new' ? '#10b981' : '#e2e8f0',
                        color: activeTab === 'sora-new' ? 'white' : '#64748b',
                        pl: 3,
                        pr: 6,
                        py: 0.75,
                        width: 'fit-content',
                        cursor: 'pointer',
                        clipPath: 'polygon(0% 0%, calc(100% - 12px) 0%, 100% 50%, calc(100% - 12px) 100%, 0% 100%, 12px 50%)',
                        display: 'flex',
                        alignItems: 'center',
                        overflow: 'hidden',
                        transition: 'all 0.3s',
                        '&::after': activeTab === 'sora-new' ? {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          bottom: 0,
                          width: '6px',
                          bgcolor: 'rgba(255, 255, 255, 0.4)',
                          transform: 'skewX(-20deg)',
                          animation: `${shine} 1.5s infinite linear`
                        } : {}
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight={700}>
                        sora new
                      </Typography>
                    </Box>
                  </>
                ) : currentModule === 'grok' ? (
                  /* Grok Sub-Tabs */
                  <>
                    <Box
                      onClick={() => setGrokSubTab('grok-10s')}
                      sx={{
                        position: 'relative',
                        bgcolor: grokSubTab === 'grok-10s' ? '#db2777' : '#e2e8f0',
                        color: grokSubTab === 'grok-10s' ? 'white' : '#64748b',
                        pl: 3,
                        pr: 6,
                        py: 0.75,
                        width: 'fit-content',
                        cursor: 'pointer',
                        clipPath: 'polygon(0% 0%, calc(100% - 12px) 0%, 100% 50%, calc(100% - 12px) 100%, 0% 100%, 12px 50%)',
                        display: 'flex',
                        alignItems: 'center',
                        overflow: 'hidden',
                        transition: 'all 0.3s',
                        '&::after': grokSubTab === 'grok-10s' ? {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          bottom: 0,
                          width: '6px',
                          bgcolor: 'rgba(255, 255, 255, 0.4)',
                          transform: 'skewX(-20deg)',
                          animation: `${shine} 1.5s infinite linear`
                        } : {}
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight={700}>Grok</Typography>
                    </Box>

                    <Box
                      onClick={() => setGrokSubTab('grok-30s')}
                      sx={{
                        position: 'relative',
                        bgcolor: grokSubTab === 'grok-30s' ? '#db2777' : '#e2e8f0',
                        color: grokSubTab === 'grok-30s' ? 'white' : '#64748b',
                        pl: 3.5,
                        pr: 5,
                        py: 0.75,
                        width: 'fit-content',
                        cursor: 'pointer',
                        clipPath: 'polygon(0% 0%, calc(100% - 12px) 0%, 100% 50%, calc(100% - 12px) 100%, 0% 100%, 12px 50%)',
                        display: 'flex',
                        alignItems: 'center',
                        overflow: 'visible',
                        transition: 'all 0.3s',
                        '&::after': grokSubTab === 'grok-30s' ? {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          bottom: 0,
                          width: '6px',
                          bgcolor: 'rgba(255, 255, 255, 0.4)',
                          transform: 'skewX(-20deg)',
                          animation: `${shine} 1.5s infinite linear`
                        } : {}
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight={700}>30s Grok</Typography>
                      {/* 优惠渠道标记 */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          right: 8,
                          bgcolor: '#10b981',
                          color: 'white',
                          fontSize: '0.6rem',
                          fontWeight: 700,
                          px: 0.75,
                          py: 0.15,
                          borderRadius: '4px',
                          whiteSpace: 'nowrap',
                          boxShadow: '0 2px 4px rgba(16,185,129,0.3)',
                          lineHeight: 1,
                        }}
                      >
                        优惠渠道
                      </Box>
                    </Box>

                    {/* 20s Grok 特惠(newapi.medu.chat 中转) */}
                    <Box
                      onClick={() => setGrokSubTab('grok-20s')}
                      sx={{
                        position: 'relative',
                        bgcolor: grokSubTab === 'grok-20s' ? '#db2777' : '#e2e8f0',
                        color: grokSubTab === 'grok-20s' ? 'white' : '#64748b',
                        pl: 3.5,
                        pr: 5,
                        py: 0.75,
                        width: 'fit-content',
                        cursor: 'pointer',
                        clipPath: 'polygon(0% 0%, calc(100% - 12px) 0%, 100% 50%, calc(100% - 12px) 100%, 0% 100%, 12px 50%)',
                        display: 'flex',
                        alignItems: 'center',
                        overflow: 'visible',
                        transition: 'all 0.3s',
                        '&::after': grokSubTab === 'grok-20s' ? {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          bottom: 0,
                          width: '6px',
                          bgcolor: 'rgba(255, 255, 255, 0.4)',
                          transform: 'skewX(-20deg)',
                          animation: `${shine} 1.5s infinite linear`
                        } : {}
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight={700}>20s Grok</Typography>
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          right: 8,
                          bgcolor: '#0ea5e9',
                          color: 'white',
                          fontSize: '0.6rem',
                          fontWeight: 700,
                          px: 0.75,
                          py: 0.15,
                          borderRadius: '4px',
                          whiteSpace: 'nowrap',
                          boxShadow: '0 2px 4px rgba(14,165,233,0.3)',
                          lineHeight: 1,
                        }}
                      >
                        特惠
                      </Box>
                    </Box>

                    {/* 短 Grok 极速档(异步 /v1/videos,6s/10s 二选一,失败原因实时透传) */}
                    <Box
                      onClick={() => setGrokSubTab('grok-short')}
                      sx={{
                        position: 'relative',
                        bgcolor: grokSubTab === 'grok-short' ? '#db2777' : '#e2e8f0',
                        color: grokSubTab === 'grok-short' ? 'white' : '#64748b',
                        pl: 3.5,
                        pr: 5,
                        py: 0.75,
                        width: 'fit-content',
                        cursor: 'pointer',
                        clipPath: 'polygon(0% 0%, calc(100% - 12px) 0%, 100% 50%, calc(100% - 12px) 100%, 0% 100%, 12px 50%)',
                        display: 'flex',
                        alignItems: 'center',
                        overflow: 'visible',
                        transition: 'all 0.3s',
                        '&::after': grokSubTab === 'grok-short' ? {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          bottom: 0,
                          width: '6px',
                          bgcolor: 'rgba(255, 255, 255, 0.4)',
                          transform: 'skewX(-20deg)',
                          animation: `${shine} 1.5s infinite linear`
                        } : {}
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight={700}>短 Grok</Typography>
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          right: 8,
                          bgcolor: '#10b981',
                          color: 'white',
                          fontSize: '0.6rem',
                          fontWeight: 700,
                          px: 0.75,
                          py: 0.15,
                          borderRadius: '4px',
                          whiteSpace: 'nowrap',
                          boxShadow: '0 2px 4px rgba(16,185,129,0.3)',
                          lineHeight: 1,
                        }}
                      >
                        极速
                      </Box>
                    </Box>

                    {/* 稳 Grok 稳定档(apimart 中转) */}
                    <Box
                      onClick={() => setGrokSubTab('grok-stable')}
                      sx={{
                        position: 'relative',
                        bgcolor: grokSubTab === 'grok-stable' ? '#db2777' : '#e2e8f0',
                        color: grokSubTab === 'grok-stable' ? 'white' : '#64748b',
                        pl: 3.5,
                        pr: 5,
                        py: 0.75,
                        width: 'fit-content',
                        cursor: 'pointer',
                        clipPath: 'polygon(0% 0%, calc(100% - 12px) 0%, 100% 50%, calc(100% - 12px) 100%, 0% 100%, 12px 50%)',
                        display: 'flex',
                        alignItems: 'center',
                        overflow: 'visible',
                        transition: 'all 0.3s',
                        '&::after': grokSubTab === 'grok-stable' ? {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          bottom: 0,
                          width: '6px',
                          bgcolor: 'rgba(255, 255, 255, 0.4)',
                          transform: 'skewX(-20deg)',
                          animation: `${shine} 1.5s infinite linear`
                        } : {}
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight={700}>稳</Typography>
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          right: 8,
                          bgcolor: '#10b981',
                          color: 'white',
                          fontSize: '0.6rem',
                          fontWeight: 700,
                          px: 0.75,
                          py: 0.15,
                          borderRadius: '4px',
                          whiteSpace: 'nowrap',
                          boxShadow: '0 2px 4px rgba(16,185,129,0.3)',
                          lineHeight: 1,
                        }}
                      >
                        稳定
                      </Box>
                    </Box>
                  </>
                ) : currentModule === 'seedance' ? (
                  /* Seedance 2.0 Fast — 五种模式切换;mmref/edit 共用 image+video+audio 三类输入 */
                  <>
                    {([
                      { key: 't2v',   label: '文生',       hint: '纯文本' },
                      { key: 'i2v',   label: '图生',       hint: '1 张首帧' },
                      { key: 'flf',   label: '首尾帧',     hint: '2 张' },
                      { key: 'mmref', label: '多模态参考', hint: '图/视/音' },
                      { key: 'edit',  label: '编辑视频',   hint: 'New' },
                    ] as const).map(({ key, label, hint }) => (
                      <Box
                        key={key}
                        onClick={() => setSeedanceMode(key)}
                        sx={{
                          position: 'relative',
                          bgcolor: seedanceMode === key ? '#7c3aed' : '#e2e8f0',
                          color: seedanceMode === key ? 'white' : '#64748b',
                          pl: 3.5,
                          pr: 5,
                          py: 0.75,
                          width: 'fit-content',
                          cursor: 'pointer',
                          clipPath: 'polygon(0% 0%, calc(100% - 12px) 0%, 100% 50%, calc(100% - 12px) 100%, 0% 100%, 12px 50%)',
                          display: 'flex',
                          alignItems: 'center',
                          overflow: 'visible',
                          transition: 'all 0.3s',
                          '&::after': seedanceMode === key ? {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            bottom: 0,
                            width: '6px',
                            bgcolor: 'rgba(255, 255, 255, 0.4)',
                            transform: 'skewX(-20deg)',
                            animation: `${shine} 1.5s infinite linear`
                          } : {}
                        }}
                      >
                        <Typography variant="subtitle1" fontWeight={700}>{label}</Typography>
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            right: 8,
                            bgcolor: '#10b981',
                            color: 'white',
                            fontSize: '0.6rem',
                            fontWeight: 700,
                            px: 0.75,
                            py: 0.15,
                            borderRadius: '4px',
                            whiteSpace: 'nowrap',
                            boxShadow: '0 2px 4px rgba(16,185,129,0.3)',
                            lineHeight: 1,
                          }}
                        >
                          {hint}
                        </Box>
                      </Box>
                    ))}
                  </>
                ) : currentModule === 'nano' ? (
                  /* Nano Tab */
                  <Box
                    sx={{
                      position: 'relative',
                      bgcolor: '#f59e0b',
                      color: 'white',
                      pl: 3,
                      pr: 3,
                      py: 0.75,
                      width: 'fit-content',
                      clipPath: 'polygon(0% 0%, calc(100% - 12px) 0%, 100% 50%, calc(100% - 12px) 100%, 0% 100%, 12px 50%)',
                      display: 'flex',
                      alignItems: 'center',
                      overflow: 'hidden',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        width: '6px',
                        bgcolor: 'rgba(255, 255, 255, 0.4)',
                        transform: 'skewX(-20deg)',
                        animation: `${shine} 1.5s infinite linear`
                      }
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={700}>
                      Nano Banana
                    </Typography>
                  </Box>
                ) : (
                  /* GPT Image Tab */
                  <Box
                    sx={{
                      position: 'relative',
                      bgcolor: '#6366f1',
                      color: 'white',
                      pl: 3,
                      pr: 3,
                      py: 0.75,
                      width: 'fit-content',
                      clipPath: 'polygon(0% 0%, calc(100% - 12px) 0%, 100% 50%, calc(100% - 12px) 100%, 0% 100%, 12px 50%)',
                      display: 'flex',
                      alignItems: 'center',
                      overflow: 'hidden',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        width: '6px',
                        bgcolor: 'rgba(255, 255, 255, 0.4)',
                        transform: 'skewX(-20deg)',
                        animation: `${shine} 1.5s infinite linear`
                      }
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={700}>
                      GPT Image
                    </Typography>
                  </Box>
                )}
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3, flex: 1, mt: 1 }}>
                {/* 左侧配置区域 */}
                <Box sx={{ flex: { xs: '1 1 auto', lg: '0 0 35%' }, display: 'flex', flexDirection: 'column', minHeight: 600 }}>
                  <Box component="form" noValidate autoComplete="off" display="flex" flexDirection="column" gap={3} sx={{ flex: 1 }}>
                    <TextField
                      label={currentModule === 'grok' ? 'Prompt (提示词 - 必填)' : 'Prompt (提示词)'}
                      multiline
                      rows={4}
                      placeholder="Describe the image you want to create..."
                      fullWidth
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      variant="outlined"
                      sx={{ bgcolor: '#f8fafc' }}
                    />

                    {currentModule === 'veo' ? (
                      /* Veo 表单字段 */
                      <>
                        {/* 仅当模型支持图片输入时显示 (veo2-fast-frames 或 veo3 系列 或 veo3+ 系列) */}
                        {(selectedModel === 'veo2-fast-frames' || selectedModel.startsWith('veo3') || selectedModel.startsWith('veo_3_1')) && (
                          <>
                            {/* 上传框并行显示：首帧、尾帧、中间帧 */}
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              {/* 本地上传：首帧 Image 1 */}
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: 110 }}>
                                <Typography variant="caption" color="text.secondary">参考图1</Typography>
                                <Box
                                  sx={{
                                    position: 'relative',
                                    width: '100%',
                                    aspectRatio: '1/1',
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    '& .clear-btn': { opacity: 0, transition: 'opacity 150ms ease' },
                                    '&:hover .clear-btn': { opacity: 1 },
                                  }}
                                >
                                  <Button
                                    variant="outlined"
                                    component="label"
                                    disabled={uploading1}
                                    sx={{
                                      width: '100%',
                                      height: '100%',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      border: '2px dashed #e2e8f0',
                                      borderRadius: 2,
                                      color: 'text.secondary',
                                      overflow: 'hidden',
                                      p: 0,
                                      '&:hover': {
                                        border: '2px dashed #2563eb',
                                        bgcolor: 'rgba(37, 99, 235, 0.04)'
                                      }
                                    }}
                                  >
                                    {image1 ? (
                                      <Box
                                        component="img"
                                        src={image1}
                                        alt="首帧预览"
                                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                      />
                                    ) : (
                                      <>
                                        {uploading1 ? (
                                          <CircularProgress size={24} />
                                        ) : (
                                          <AddPhotoAlternateIcon sx={{ fontSize: 32, mb: 0.5, color: '#cbd5e1' }} />
                                        )}
                                        <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                                          {uploading1 ? '上传中...' : '上传图片'}
                                        </Typography>
                                      </>
                                    )}
                                    <input type="file" accept="image/*" hidden onChange={async (e) => {
                                      const file = e.target.files?.[0];
                                      if (!file) return;
                                      setUploading1(true);
                                      try {
                                        const url = await uploadReferenceImage(file);
                                        setImage1(url);
                                        setSnackbarMsg('首帧图片上传成功');
                                        setSnackbarOpen(true);
                                      } catch (err: any) {
                                        setSnackbarMsg(err.message || '首帧上传失败');
                                        setSnackbarOpen(true);
                                      } finally {
                                        setUploading1(false);
                                        e.target.value = '';
                                      }
                                    }} />
                                  </Button>
                                  {image1 && (
                                    <IconButton
                                      className="clear-btn"
                                      size="small"
                                      onClick={() => setImage1('')}
                                      sx={{
                                        position: 'absolute',
                                        top: 4,
                                        right: 4,
                                        zIndex: 2,
                                        width: 20,
                                        height: 20,
                                        p: 0,
                                        bgcolor: 'rgba(15, 23, 42, 0.65)',
                                        color: 'white',
                                        borderRadius: '50%',
                                        '&:hover': { bgcolor: 'rgba(220, 38, 38, 0.85)' },
                                      }}
                                    >
                                      <CloseIcon sx={{ fontSize: 12 }} />
                                    </IconButton>
                                  )}
                                </Box>
                              </Box>

                              {/* 本地上传：尾帧 Image 2 */}
                              {selectedModel !== 'veo3-pro-frames' && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: 110 }}>
                                  <Typography variant="caption" color="text.secondary">参考图2</Typography>
                                  <Box
                                    sx={{
                                      position: 'relative',
                                      width: '100%',
                                      aspectRatio: '1/1',
                                      borderRadius: 2,
                                      overflow: 'hidden',
                                      '& .clear-btn': { opacity: 0, transition: 'opacity 150ms ease' },
                                      '&:hover .clear-btn': { opacity: 1 },
                                    }}
                                  >
                                    <Button
                                      variant="outlined"
                                      component="label"
                                      disabled={uploading2}
                                      sx={{
                                        width: '100%',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: '2px dashed #e2e8f0',
                                        borderRadius: 2,
                                        color: 'text.secondary',
                                        overflow: 'hidden',
                                        p: 0,
                                        '&:hover': {
                                          border: '2px dashed #2563eb',
                                          bgcolor: 'rgba(37, 99, 235, 0.04)'
                                        }
                                      }}
                                    >
                                      {image2 ? (
                                        <Box
                                          component="img"
                                          src={image2}
                                          alt="尾帧预览"
                                          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                      ) : (
                                        <>
                                          {uploading2 ? (
                                            <CircularProgress size={24} />
                                          ) : (
                                            <AddPhotoAlternateIcon sx={{ fontSize: 32, mb: 0.5, color: '#cbd5e1' }} />
                                          )}
                                          <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                                            {uploading2 ? '上传中...' : '上传图片'}
                                          </Typography>
                                        </>
                                      )}
                                      <input type="file" accept="image/*" hidden onChange={async (e) => {
                                        const file = e.target.files?.[0];
                                        if (!file) return;
                                        setUploading2(true);
                                        try {
                                          const url = await uploadReferenceImage(file);
                                          setImage2(url);
                                          setSnackbarMsg('尾帧图片上传成功');
                                          setSnackbarOpen(true);
                                        } catch (err: any) {
                                          setSnackbarMsg(err.message || '尾帧上传失败');
                                          setSnackbarOpen(true);
                                        } finally {
                                          setUploading2(false);
                                          e.target.value = '';
                                        }
                                      }} />
                                    </Button>
                                    {image2 && (
                                      <IconButton
                                        className="clear-btn"
                                        size="small"
                                        onClick={() => setImage2('')}
                                        sx={{
                                          position: 'absolute',
                                          top: 4,
                                          right: 4,
                                          zIndex: 2,
                                          width: 20,
                                          height: 20,
                                          p: 0,
                                          bgcolor: 'rgba(15, 23, 42, 0.65)',
                                          color: 'white',
                                          borderRadius: '50%',
                                          '&:hover': { bgcolor: 'rgba(220, 38, 38, 0.85)' },
                                        }}
                                      >
                                        <CloseIcon sx={{ fontSize: 12 }} />
                                      </IconButton>
                                    )}
                                  </Box>
                                </Box>
                              )}

                              {/* 本地上传：中间帧 Image 3 */}
                              {(selectedModel === 'veo3.1-components' || selectedModel === 'veo_3_1-components-4K') && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: 110 }}>
                                  <Typography variant="caption" color="text.secondary">参考图3</Typography>
                                  <Box
                                    sx={{
                                      position: 'relative',
                                      width: '100%',
                                      aspectRatio: '1/1',
                                      borderRadius: 2,
                                      overflow: 'hidden',
                                      '& .clear-btn': { opacity: 0, transition: 'opacity 150ms ease' },
                                      '&:hover .clear-btn': { opacity: 1 },
                                    }}
                                  >
                                    <Button
                                      variant="outlined"
                                      component="label"
                                      disabled={uploading3}
                                      sx={{
                                        width: '100%',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: '2px dashed #e2e8f0',
                                        borderRadius: 2,
                                        color: 'text.secondary',
                                        overflow: 'hidden',
                                        p: 0,
                                        '&:hover': {
                                          border: '2px dashed #2563eb',
                                          bgcolor: 'rgba(37, 99, 235, 0.04)'
                                        }
                                      }}
                                    >
                                      {image3 ? (
                                        <Box
                                          component="img"
                                          src={image3}
                                          alt="中间帧预览"
                                          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                      ) : (
                                        <>
                                          {uploading3 ? (
                                            <CircularProgress size={24} />
                                          ) : (
                                            <AddPhotoAlternateIcon sx={{ fontSize: 32, mb: 0.5, color: '#cbd5e1' }} />
                                          )}
                                          <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                                            {uploading3 ? '上传中...' : '上传图片'}
                                          </Typography>
                                        </>
                                      )}
                                      <input type="file" accept="image/*" hidden onChange={async (e) => {
                                        const file = e.target.files?.[0];
                                        if (!file) return;
                                        setUploading3(true);
                                        try {
                                          const url = await uploadReferenceImage(file);
                                          setImage3(url);
                                          setSnackbarMsg('中间帧图片上传成功');
                                          setSnackbarOpen(true);
                                        } catch (err: any) {
                                          setSnackbarMsg(err.message || '中间帧上传失败');
                                          setSnackbarOpen(true);
                                        } finally {
                                          setUploading3(false);
                                          e.target.value = '';
                                        }
                                      }} />
                                    </Button>
                                    {image3 && (
                                      <IconButton
                                        className="clear-btn"
                                        size="small"
                                        onClick={() => setImage3('')}
                                        sx={{
                                          position: 'absolute',
                                          top: 4,
                                          right: 4,
                                          zIndex: 2,
                                          width: 20,
                                          height: 20,
                                          p: 0,
                                          bgcolor: 'rgba(15, 23, 42, 0.65)',
                                          color: 'white',
                                          borderRadius: '50%',
                                          '&:hover': { bgcolor: 'rgba(220, 38, 38, 0.85)' },
                                        }}
                                      >
                                        <CloseIcon sx={{ fontSize: 12 }} />
                                      </IconButton>
                                    )}
                                  </Box>
                                </Box>
                              )}
                            </Box>

                            <Typography variant="caption" color="text.secondary">
                              {selectedModel === 'veo3-pro-frames'
                                ? '* 仅支持上传一张图片作为参考。'
                                : (selectedModel === 'veo3.1-components' || selectedModel === 'veo_3_1-components-4K')
                                  ? '* 支持上传最多三张图片作为参考。'
                                  : '* 只传一张图片作为首帧参考，传两张则分别为首帧和尾帧。（veo2-fast-frames 支持本地上传）'}
                            </Typography>
                          </>
                        )}

                        <Box display="flex" gap={2}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Aspect Ratio</InputLabel>
                            <Select
                              value={aspectRatio}
                              label="Aspect Ratio"
                              onChange={(e) => setAspectRatio(e.target.value)}
                            >
                              <MenuItem value="16:9">Cinema (16:9)</MenuItem>
                              <MenuItem value="9:16">Portrait (9:16)</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>

                        {/* 模型选择（右侧表单控制，不联动左侧） */}
                        <FormControl fullWidth size="small">
                          <InputLabel>Model</InputLabel>
                          <Select
                            value={selectedModel}
                            label="Model"
                            onChange={(e) => setSelectedModel(e.target.value)}
                          >
                            {activeTab === 'veo2' ? [
                              <MenuItem key="veo2" value="veo2">Veo2</MenuItem>,
                              <MenuItem key="veo2-fast-frames" value="veo2-fast-frames">Veo2 Fast Frames</MenuItem>
                            ] : activeTab === 'veo3' ? [
                              <MenuItem key="veo3" value="veo3">Veo3</MenuItem>,
                              <MenuItem key="veo3-fast" value="veo3-fast">Veo3 Fast</MenuItem>,
                              <MenuItem key="veo3-frames" value="veo3-frames">Veo3 Frames</MenuItem>
                            ] : [
                              <MenuItem key="veo_3_1" value="veo_3_1">Veo 3.1（高质量）</MenuItem>,
                              <MenuItem key="veo_3_1-fast" value="veo_3_1-fast">Veo 3.1 Fast（快速）</MenuItem>,
                              <MenuItem key="veo_3_1-components-4K" value="veo_3_1-components-4K">Veo 3.1 4K</MenuItem>
                            ]}
                          </Select>
                        </FormControl>
                      </>
                    ) : currentModule === 'sora' ? (
                      /* sora new 表单字段 */
                      <>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Typography variant="caption" color="text.secondary">Reference Image (参考图片 - 可选，仅支持单张)</Typography>
                          <Box sx={{ width: 110, position: 'relative' }}>
                            <Button
                              variant="outlined"
                              component="label"
                              disabled={uploadingSora}
                              sx={{
                                width: '100%',
                                aspectRatio: '1/1',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '2px dashed #e2e8f0',
                                borderRadius: 2,
                                color: 'text.secondary',
                                overflow: 'hidden',
                                p: 0,
                                '&:hover': {
                                  border: '2px dashed #10b981',
                                  bgcolor: 'rgba(16, 185, 129, 0.04)'
                                }
                              }}
                            >
                              {soraUrl ? (
                                <Box
                                  component="img"
                                  src={soraUrl}
                                  alt="参考图预览"
                                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                              ) : (
                                <>
                                  {uploadingSora ? (
                                    <CircularProgress size={24} sx={{ color: '#10b981' }} />
                                  ) : (
                                    <AddPhotoAlternateIcon sx={{ fontSize: 32, mb: 0.5, color: '#cbd5e1' }} />
                                  )}
                                  <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                                    {uploadingSora ? '上传中...' : '上传图片'}
                                  </Typography>
                                </>
                              )}
                              <input type="file" accept="image/*" hidden onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                setUploadingSora(true);
                                try {
                                  const url = await uploadReferenceImage(file);
                                  setSoraUrl(url);
                                  setSnackbarMsg('参考图上传成功');
                                  setSnackbarOpen(true);
                                } catch (err: any) {
                                  setSnackbarMsg(err.message || '上传失败');
                                  setSnackbarOpen(true);
                                } finally {
                                  setUploadingSora(false);
                                  e.target.value = '';
                                }
                              }} />
                            </Button>
                            {soraUrl && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setSoraUrl('');
                                }}
                                sx={{
                                  position: 'absolute',
                                  top: 4,
                                  right: 4,
                                  width: 16,
                                  height: 16,
                                  minWidth: 0,
                                  bgcolor: 'rgba(0,0,0,0.5)',
                                  color: 'white',
                                  p: 0,
                                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                                }}
                              >
                                <CloseIcon sx={{ fontSize: 12 }} />
                              </IconButton>
                            )}
                          </Box>
                        </Box>

                        <FormControl fullWidth size="small">
                          <InputLabel>Aspect Ratio</InputLabel>
                          <Select
                            value={aspectRatio}
                            label="Aspect Ratio"
                            onChange={(e) => setAspectRatio(e.target.value)}
                          >
                            <MenuItem value="16:9">Cinema (16:9)</MenuItem>
                            <MenuItem value="9:16">Portrait (9:16)</MenuItem>
                          </Select>
                        </FormControl>

                        <Box
                          sx={{
                            mt: 0.5,
                            px: 1.5,
                            py: 1,
                            borderRadius: 1,
                            bgcolor: 'rgba(16, 185, 129, 0.08)',
                            border: '1px solid rgba(16, 185, 129, 0.25)'
                          }}
                        >
                          <Typography variant="caption" sx={{ color: '#0f766e', fontWeight: 600 }}>
                            时长 12s · 20 积分 · Sora2 无水印 · 失败时直接显示上游原因
                          </Typography>
                        </Box>
                      </>
                    ) : currentModule === 'grok' ? (
                      <>
                        {grokSubTab === 'grok-30s' && (
                          <FormControl fullWidth size="small">
                            <InputLabel>Duration (时长)</InputLabel>
                            <Select
                              value={grokImagineDuration}
                              label="Duration (时长)"
                              onChange={(e) => setGrokImagineDuration(Number(e.target.value))}
                            >
                              <MenuItem value={6}>6 秒（6积分）</MenuItem>
                              <MenuItem value={10}>10 秒（10积分）</MenuItem>
                              <MenuItem value={15}>15 秒（15积分）</MenuItem>
                              <MenuItem value={20}>20 秒（20积分）</MenuItem>
                              <MenuItem value={30}>30 秒（30积分）</MenuItem>
                            </Select>
                          </FormControl>
                        )}
                        {grokSubTab === 'grok-stable' && (
                          <FormControl fullWidth size="small">
                            <InputLabel>Duration (时长)</InputLabel>
                            <Select
                              value={grokStableDuration}
                              label="Duration (时长)"
                              onChange={(e) => setGrokStableDuration(Number(e.target.value))}
                            >
                              <MenuItem value={6}>6 秒（{GROK_STABLE_COST_BY_DURATION[6]}积分）</MenuItem>
                              <MenuItem value={12}>12 秒（{GROK_STABLE_COST_BY_DURATION[12]}积分）</MenuItem>
                              <MenuItem value={20}>20 秒（{GROK_STABLE_COST_BY_DURATION[20]}积分）</MenuItem>
                              <MenuItem value={30}>30 秒（{GROK_STABLE_COST_BY_DURATION[30]}积分）</MenuItem>
                            </Select>
                          </FormControl>
                        )}
                        {(grokSubTab === 'grok-20s' || grokSubTab === 'grok-short') && (
                          <>
                            <FormControl fullWidth size="small">
                              <InputLabel>Duration (时长)</InputLabel>
                              {grokSubTab === 'grok-short' ? (
                                <Select
                                  value={grokShortDuration}
                                  label="Duration (时长)"
                                  onChange={(e) => setGrokShortDuration(Number(e.target.value) as 6 | 10)}
                                >
                                  <MenuItem value={6}>6 秒（6积分 · 极速,失败直接显示原因）</MenuItem>
                                  <MenuItem value={10}>10 秒（10积分 · 极速,失败直接显示原因）</MenuItem>
                                </Select>
                              ) : (
                                <Select
                                  value={grokLiteDuration}
                                  label="Duration (时长)"
                                  onChange={(e) => setGrokLiteDuration(Number(e.target.value))}
                                >
                                  <MenuItem value={20}>20 秒（20积分 · 单次生成）</MenuItem>
                                  <MenuItem value={60}>60 秒（60积分 · 长片版,约 12 分钟）</MenuItem>
                                </Select>
                              )}
                            </FormControl>
                            <Box sx={{
                              p: 1.25,
                              bgcolor: grokSubTab === 'grok-short' ? 'rgba(16, 185, 129, 0.08)' : 'rgba(14, 165, 233, 0.08)',
                              border: grokSubTab === 'grok-short' ? '1px solid rgba(16, 185, 129, 0.25)' : '1px solid rgba(14, 165, 233, 0.25)',
                              borderRadius: 1,
                            }}>
                              <Typography variant="caption" sx={{ color: grokSubTab === 'grok-short' ? '#047857' : '#0369a1', fontWeight: 600, display: 'block', mb: 0.25 }}>
                                {grokSubTab === 'grok-short' ? 'Grok 极速通道' : 'Grok 特惠通道'}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#475569' }}>
                                {grokSubTab === 'grok-short'
                                  ? '·走异步接口 · 失败时直接显示上游原因(审核拒、敏感词等) · 只支持 9:16 / 16:9 · 参考图最多 7 张'
                                  : `·只支持 9:16 / 16:9 ·参考图最多 7 张${grokLiteDuration === 60 ? ' · 60 秒为长片版,生成失败全额退还积分' : ''}`}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                Reference Images (参考图 - 可选,最多 {GROK_LITE_MAX_IMAGES} 张)
                              </Typography>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: 110, mb: 1 }}>
                                <Button
                                  variant="outlined"
                                  component="label"
                                  disabled={uploadingGrokLite || grokLiteImages.length >= GROK_LITE_MAX_IMAGES}
                                  sx={{
                                    width: '100%',
                                    aspectRatio: '1/1',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '2px dashed #e2e8f0',
                                    borderRadius: 2,
                                    color: 'text.secondary',
                                    overflow: 'hidden',
                                    p: 0,
                                    '&:hover': {
                                      border: '2px dashed #db2777',
                                      bgcolor: 'rgba(219, 39, 119, 0.04)'
                                    }
                                  }}
                                >
                                  {uploadingGrokLite ? (
                                    <CircularProgress size={24} sx={{ color: '#db2777' }} />
                                  ) : (
                                    <>
                                      <AddPhotoAlternateIcon sx={{ fontSize: 32, mb: 0.5, color: '#cbd5e1' }} />
                                      <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                                        {grokLiteImages.length >= GROK_LITE_MAX_IMAGES ? '已达上限' : `上传 (${grokLiteImages.length}/${GROK_LITE_MAX_IMAGES})`}
                                      </Typography>
                                    </>
                                  )}
                                  <input type="file" accept="image/*" hidden onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    if (grokLiteImages.length >= GROK_LITE_MAX_IMAGES) {
                                      setSnackbarMsg(`最多上传 ${GROK_LITE_MAX_IMAGES} 张参考图`);
                                      setSnackbarOpen(true);
                                      e.target.value = '';
                                      return;
                                    }
                                    setUploadingGrokLite(true);
                                    try {
                                      const url = await uploadReferenceImage(file);
                                      if (!url) throw new Error('上传返回为空');
                                      setGrokLiteImages(prev => [...prev, url]);
                                      setSnackbarMsg('参考图上传成功');
                                      setSnackbarOpen(true);
                                    } catch (err: any) {
                                      setSnackbarMsg(err.message || '上传失败');
                                      setSnackbarOpen(true);
                                    } finally {
                                      setUploadingGrokLite(false);
                                      e.target.value = '';
                                    }
                                  }} />
                                </Button>
                              </Box>
                              {grokLiteImages.length > 0 && (
                                <Box display="flex" flexWrap="wrap" gap={1}>
                                  {grokLiteImages.map((img, idx) => (
                                    <Box key={idx} sx={{
                                      position: 'relative',
                                      width: 60, height: 60, borderRadius: 1, overflow: 'hidden',
                                      border: '1px solid #e2e8f0',
                                      '&:hover .delete-btn': { opacity: 1 }
                                    }}>
                                      <Box component="img" src={img} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                      <IconButton
                                        className="delete-btn"
                                        size="small"
                                        onClick={() => setGrokLiteImages(prev => prev.filter((_, i) => i !== idx))}
                                        sx={{
                                          position: 'absolute', top: 2, right: 2,
                                          bgcolor: 'rgba(0,0,0,0.5)', color: 'white', p: 0.25,
                                          opacity: 0, transition: 'opacity 0.2s',
                                          '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                                        }}
                                      >
                                        <CloseIcon sx={{ fontSize: 14 }} />
                                      </IconButton>
                                    </Box>
                                  ))}
                                </Box>
                              )}
                            </Box>
                          </>
                        )}
                        {(grokSubTab === 'grok-20s' || grokSubTab === 'grok-short') ? null : grokSubTab === 'grok-30s' ? (
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                              Reference Images (参考图 - 可选，最多 4 张)
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: 110, mb: 1 }}>
                              <Button
                                variant="outlined"
                                component="label"
                                disabled={uploadingGrok || grokImagineImages.length >= 4}
                                sx={{
                                  width: '100%',
                                  aspectRatio: '1/1',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: '2px dashed #e2e8f0',
                                  borderRadius: 2,
                                  color: 'text.secondary',
                                  overflow: 'hidden',
                                  p: 0,
                                  '&:hover': {
                                    border: '2px dashed #db2777',
                                    bgcolor: 'rgba(219, 39, 119, 0.04)'
                                  }
                                }}
                              >
                                {uploadingGrok ? (
                                  <CircularProgress size={24} sx={{ color: '#db2777' }} />
                                ) : (
                                  <>
                                    <AddPhotoAlternateIcon sx={{ fontSize: 32, mb: 0.5, color: '#cbd5e1' }} />
                                    <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                                      {grokImagineImages.length >= 4 ? '已达上限' : '上传图片'}
                                    </Typography>
                                  </>
                                )}
                                <input type="file" accept="image/*" hidden onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  if (grokImagineImages.length >= 4) {
                                    setSnackbarMsg('最多上传 4 张参考图');
                                    setSnackbarOpen(true);
                                    e.target.value = '';
                                    return;
                                  }
                                  setUploadingGrok(true);
                                  try {
                                    const url = await uploadReferenceImage(file);
                                    if (!url) throw new Error('上传返回为空');
                                    setGrokImagineImages(prev => [...prev, url]);
                                    setSnackbarMsg('参考图上传成功');
                                    setSnackbarOpen(true);
                                  } catch (err: any) {
                                    setSnackbarMsg(err.message || '上传失败');
                                    setSnackbarOpen(true);
                                  } finally {
                                    setUploadingGrok(false);
                                    e.target.value = '';
                                  }
                                }} />
                              </Button>
                            </Box>
                            <Box display="flex" flexWrap="wrap" gap={1}>
                              {grokImagineImages.map((img, idx) => (
                                <Box key={idx} sx={{
                                  position: 'relative',
                                  width: 60,
                                  height: 60,
                                  borderRadius: 1,
                                  overflow: 'hidden',
                                  border: '1px solid #e2e8f0',
                                  '&:hover .delete-btn': { opacity: 1 }
                                }}>
                                  <Box component="img" src={img} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                  <IconButton
                                    className="delete-btn"
                                    size="small"
                                    onClick={() => {
                                      setGrokImagineImages(prev => prev.filter((_, i) => i !== idx));
                                    }}
                                    sx={{
                                      position: 'absolute',
                                      top: 2,
                                      right: 2,
                                      bgcolor: 'rgba(0,0,0,0.5)',
                                      color: 'white',
                                      p: 0.25,
                                      opacity: 0,
                                      transition: 'opacity 0.2s',
                                      '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                                    }}
                                  >
                                    <CloseIcon sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        ) : grokSubTab === 'grok-stable' ? (
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                              Reference Images (参考图 - 可选，最多 7 张)
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: 110, mb: 1 }}>
                              <Button
                                variant="outlined"
                                component="label"
                                disabled={uploadingGrok || grokStableImages.length >= 7}
                                sx={{
                                  width: '100%',
                                  aspectRatio: '1/1',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: '2px dashed #e2e8f0',
                                  borderRadius: 2,
                                  color: 'text.secondary',
                                  overflow: 'hidden',
                                  p: 0,
                                  '&:hover': {
                                    border: '2px dashed #db2777',
                                    bgcolor: 'rgba(219, 39, 119, 0.04)'
                                  }
                                }}
                              >
                                {uploadingGrok ? (
                                  <CircularProgress size={24} sx={{ color: '#db2777' }} />
                                ) : (
                                  <>
                                    <AddPhotoAlternateIcon sx={{ fontSize: 32, mb: 0.5, color: '#cbd5e1' }} />
                                    <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                                      {grokStableImages.length >= 7 ? '已达上限' : '上传图片'}
                                    </Typography>
                                  </>
                                )}
                                <input type="file" accept="image/*" hidden onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  if (grokStableImages.length >= 7) {
                                    setSnackbarMsg('最多上传 7 张参考图');
                                    setSnackbarOpen(true);
                                    e.target.value = '';
                                    return;
                                  }
                                  setUploadingGrok(true);
                                  try {
                                    const url = await uploadReferenceImage(file);
                                    if (!url) throw new Error('上传返回为空');
                                    setGrokStableImages(prev => {
                                      const next = [...prev, url];
                                      return next.length <= 7 ? next : prev;
                                    });
                                    setSnackbarMsg('参考图上传成功');
                                    setSnackbarOpen(true);
                                  } catch (err: any) {
                                    setSnackbarMsg(err.message || '上传失败');
                                    setSnackbarOpen(true);
                                  } finally {
                                    setUploadingGrok(false);
                                    e.target.value = '';
                                  }
                                }} />
                              </Button>
                            </Box>
                            <Box display="flex" flexWrap="wrap" gap={1}>
                              {grokStableImages.map((img, idx) => (
                                <Box key={idx} sx={{
                                  position: 'relative',
                                  width: 60,
                                  height: 60,
                                  borderRadius: 1,
                                  overflow: 'hidden',
                                  border: '1px solid #e2e8f0',
                                  '&:hover .delete-btn': { opacity: 1 }
                                }}>
                                  <Box component="img" src={img} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                  <IconButton
                                    className="delete-btn"
                                    size="small"
                                    onClick={() => {
                                      setGrokStableImages(prev => prev.filter((_, i) => i !== idx));
                                    }}
                                    sx={{
                                      position: 'absolute',
                                      top: 2,
                                      right: 2,
                                      bgcolor: 'rgba(0,0,0,0.5)',
                                      color: 'white',
                                      p: 0.25,
                                      opacity: 0,
                                      transition: 'opacity 0.2s',
                                      '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                                    }}
                                  >
                                    <CloseIcon sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Typography variant="caption" color="text.secondary">Reference Image (参考图片 - 可选)</Typography>
                            <Box sx={{ width: 110, position: 'relative' }}>
                              <Button
                                variant="outlined"
                                component="label"
                                disabled={uploadingGrok}
                                sx={{
                                  width: '100%',
                                  aspectRatio: '1/1',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: '2px dashed #e2e8f0',
                                  borderRadius: 2,
                                  color: 'text.secondary',
                                  overflow: 'hidden',
                                  p: 0,
                                  '&:hover': {
                                    border: '2px dashed #db2777',
                                    bgcolor: 'rgba(219, 39, 119, 0.04)'
                                  }
                                }}
                              >
                                {grokUrl ? (
                                  <Box
                                    component="img"
                                    src={grokUrl}
                                    alt="Grok 参考图预览"
                                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                  />
                                ) : (
                                  <>
                                    {uploadingGrok ? (
                                      <CircularProgress size={24} sx={{ color: '#db2777' }} />
                                    ) : (
                                      <AddPhotoAlternateIcon sx={{ fontSize: 32, mb: 0.5, color: '#cbd5e1' }} />
                                    )}
                                    <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                                      {uploadingGrok ? '上传中...' : '上传图片'}
                                    </Typography>
                                  </>
                                )}
                                <input type="file" accept="image/*" hidden onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  setUploadingGrok(true);
                                  try {
                                    const url = await uploadReferenceImage(file);
                                    if (!url) throw new Error('上传返回为空');
                                    setGrokUrl(url);
                                    setSnackbarMsg('Grok 参考图上传成功');
                                    setSnackbarOpen(true);
                                  } catch (err: any) {
                                    setSnackbarMsg(err.message || '上传失败');
                                    setSnackbarOpen(true);
                                  } finally {
                                    setUploadingGrok(false);
                                    e.target.value = '';
                                  }
                                }} />
                              </Button>
                              {grokUrl && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    setGrokUrl('');
                                  }}
                                  sx={{
                                    position: 'absolute',
                                    top: 4,
                                    right: 4,
                                    width: 16,
                                    height: 16,
                                    minWidth: 0,
                                    bgcolor: 'rgba(0,0,0,0.5)',
                                    color: 'white',
                                    p: 0,
                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                                  }}
                                >
                                  <CloseIcon sx={{ fontSize: 12 }} />
                                </IconButton>
                              )}
                            </Box>
                          </Box>
                        )}

                        <FormControl fullWidth size="small">
                          <InputLabel>Aspect Ratio</InputLabel>
                          <Select
                            value={
                              grokSubTab === 'grok-20s' && !['9:16', '16:9'].includes(aspectRatio)
                                ? '9:16'
                                : aspectRatio
                            }
                            label="Aspect Ratio"
                            onChange={(e) => setAspectRatio(e.target.value)}
                          >
                            {grokSubTab === 'grok-20s' ? (
                              [
                                <MenuItem key="9:16" value="9:16">9:16 (Portrait 竖屏)</MenuItem>,
                                <MenuItem key="16:9" value="16:9">16:9 (Landscape 横屏)</MenuItem>,
                              ]
                            ) : (
                              [
                                <MenuItem key="1:1" value="1:1">1:1</MenuItem>,
                                <MenuItem key="16:9" value="16:9">16:9</MenuItem>,
                                <MenuItem key="9:16" value="9:16">9:16</MenuItem>,
                                <MenuItem key="4:3" value="4:3">4:3</MenuItem>,
                                <MenuItem key="3:4" value="3:4">3:4</MenuItem>,
                              ]
                            )}
                          </Select>
                        </FormControl>
                      </>
                    ) : currentModule === 'seedance' ? (
                      /* Seedance 2.0 Fast 表单字段 — 按模式动态显示图片输入 */
                      <>
                        <FormControl fullWidth size="small">
                          <InputLabel>Duration (时长)</InputLabel>
                          <Select
                            value={seedanceDuration}
                            label="Duration (时长)"
                            onChange={(e) => setSeedanceDuration(Number(e.target.value) as 5 | 10 | 15)}
                          >
                            <MenuItem value={5}>5 秒（{SEEDANCE_COST_BY_DURATION[5]}积分）</MenuItem>
                            <MenuItem value={10}>10 秒（{SEEDANCE_COST_BY_DURATION[10]}积分）</MenuItem>
                            <MenuItem value={15}>15 秒（{SEEDANCE_COST_BY_DURATION[15]}积分）</MenuItem>
                          </Select>
                        </FormControl>

                        <FormControl fullWidth size="small">
                          <InputLabel>Aspect Ratio</InputLabel>
                          <Select
                            value={aspectRatio}
                            label="Aspect Ratio"
                            onChange={(e) => setAspectRatio(e.target.value)}
                          >
                            <MenuItem value="16:9">16:9 横屏</MenuItem>
                            <MenuItem value="9:16">9:16 竖屏</MenuItem>
                            <MenuItem value="1:1">1:1 方图</MenuItem>
                            <MenuItem value="4:3">4:3</MenuItem>
                            <MenuItem value="3:4">3:4</MenuItem>
                            <MenuItem value="21:9">21:9 宽屏</MenuItem>
                            <MenuItem value="adaptive">adaptive 自适应</MenuItem>
                          </Select>
                        </FormControl>

                        <Box sx={{
                          p: 1.25,
                          bgcolor: 'rgba(139, 92, 246, 0.08)',
                          border: '1px solid rgba(139, 92, 246, 0.25)',
                          borderRadius: 1,
                        }}>
                          <Typography variant="caption" sx={{ color: '#6d28d9', fontWeight: 600, display: 'block', mb: 0.25 }}>
                            {seedanceMode === 't2v' ? '文生视频模式'
                              : seedanceMode === 'i2v' ? '图生视频模式(单首帧)'
                              : seedanceMode === 'flf' ? '首尾帧模式(2 张图)'
                              : seedanceMode === 'mmref' ? `多模态参考模式(图最多 ${SEEDANCE_MAX_REFERENCE_IMAGES} 张 + 视频 + 音频,至少 1 类)`
                              : `编辑视频模式(视频必填 + 可选 ${SEEDANCE_EDIT_MAX_IMAGES} 张参考图)`}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#475569' }}>
                            {seedanceMode === 't2v' && '·纯文本生成视频 · doubao-seedance-2.0-fast'}
                            {seedanceMode === 'i2v' && '·上传 1 张首帧图,生成基于该图的视频'}
                            {seedanceMode === 'flf' && '·上传首帧 + 尾帧,模型在两图之间过渡'}
                            {seedanceMode === 'mmref' && '·图/视频/音频任意组合,模型综合参考。Prompt 可用「图1/视频1/音频1」引用'}
                            {seedanceMode === 'edit' && '·上传待编辑的视频,Prompt 写编辑指令(替换主体/增删元素/局部重绘等)。可选参考图引导风格'}
                          </Typography>
                        </Box>

                        {/* Audio toggle — fast 档原生支持 audio 生成 */}
                        <FormControlLabel
                          control={
                            <Switch
                              checked={seedanceGenerateAudio}
                              onChange={(e) => setSeedanceGenerateAudio(e.target.checked)}
                              size="small"
                            />
                          }
                          label={<Typography variant="caption" color="text.secondary">同步生成音频 (generate_audio)</Typography>}
                        />

                        {/* 单图首帧上传:i2v 模式 */}
                        {seedanceMode === 'i2v' && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                              首帧图 (必填)
                            </Typography>
                            <Box sx={{ width: 110 }}>
                              <Button
                                variant="outlined"
                                component="label"
                                disabled={uploadingSeedanceFirst}
                                sx={{
                                  width: '100%', aspectRatio: '1/1', display: 'flex', flexDirection: 'column',
                                  alignItems: 'center', justifyContent: 'center',
                                  border: '2px dashed #e2e8f0', borderRadius: 2, color: 'text.secondary',
                                  overflow: 'hidden', p: 0,
                                  '&:hover': { border: '2px dashed #7c3aed', bgcolor: 'rgba(124, 58, 237, 0.04)' }
                                }}
                              >
                                {seedanceFirstFrame ? (
                                  <Box component="img" src={seedanceFirstFrame} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : uploadingSeedanceFirst ? (
                                  <CircularProgress size={24} sx={{ color: '#7c3aed' }} />
                                ) : (
                                  <>
                                    <AddPhotoAlternateIcon sx={{ fontSize: 32, mb: 0.5, color: '#cbd5e1' }} />
                                    <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                                      上传首帧
                                    </Typography>
                                  </>
                                )}
                                <input type="file" accept="image/*" hidden onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  setUploadingSeedanceFirst(true);
                                  try {
                                    const url = await uploadReferenceImage(file);
                                    if (!url) throw new Error('上传返回为空');
                                    setSeedanceFirstFrame(url);
                                    setSnackbarMsg('首帧图上传成功');
                                    setSnackbarOpen(true);
                                  } catch (err: any) {
                                    setSnackbarMsg(err.message || '上传失败');
                                    setSnackbarOpen(true);
                                  } finally {
                                    setUploadingSeedanceFirst(false);
                                    e.target.value = '';
                                  }
                                }} />
                              </Button>
                              {seedanceFirstFrame && (
                                <Button size="small" onClick={() => setSeedanceFirstFrame('')} sx={{ mt: 0.5, fontSize: '0.7rem' }}>清除</Button>
                              )}
                            </Box>
                          </Box>
                        )}

                        {/* 首尾帧:flf 模式 */}
                        {seedanceMode === 'flf' && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                              首帧 + 尾帧 (两张都必填)
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1.5 }}>
                              {([
                                { url: seedanceFirstFrame, setUrl: setSeedanceFirstFrame,
                                  uploading: uploadingSeedanceFirst, setUploading: setUploadingSeedanceFirst,
                                  label: '首帧' },
                                { url: seedanceLastFrame, setUrl: setSeedanceLastFrame,
                                  uploading: uploadingSeedanceLast, setUploading: setUploadingSeedanceLast,
                                  label: '尾帧' },
                              ]).map((slot, idx) => (
                                <Box key={idx} sx={{ width: 100 }}>
                                  <Button
                                    variant="outlined"
                                    component="label"
                                    disabled={slot.uploading}
                                    sx={{
                                      width: '100%', aspectRatio: '1/1', display: 'flex', flexDirection: 'column',
                                      alignItems: 'center', justifyContent: 'center',
                                      border: '2px dashed #e2e8f0', borderRadius: 2, color: 'text.secondary',
                                      overflow: 'hidden', p: 0,
                                      '&:hover': { border: '2px dashed #7c3aed', bgcolor: 'rgba(124, 58, 237, 0.04)' }
                                    }}
                                  >
                                    {slot.url ? (
                                      <Box component="img" src={slot.url} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    ) : slot.uploading ? (
                                      <CircularProgress size={20} sx={{ color: '#7c3aed' }} />
                                    ) : (
                                      <>
                                        <AddPhotoAlternateIcon sx={{ fontSize: 28, mb: 0.5, color: '#cbd5e1' }} />
                                        <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                                          {slot.label}
                                        </Typography>
                                      </>
                                    )}
                                    <input type="file" accept="image/*" hidden onChange={async (e) => {
                                      const file = e.target.files?.[0];
                                      if (!file) return;
                                      slot.setUploading(true);
                                      try {
                                        const url = await uploadReferenceImage(file);
                                        if (!url) throw new Error('上传返回为空');
                                        slot.setUrl(url);
                                        setSnackbarMsg(`${slot.label}上传成功`);
                                        setSnackbarOpen(true);
                                      } catch (err: any) {
                                        setSnackbarMsg(err.message || '上传失败');
                                        setSnackbarOpen(true);
                                      } finally {
                                        slot.setUploading(false);
                                        e.target.value = '';
                                      }
                                    }} />
                                  </Button>
                                  {slot.url && (
                                    <Button size="small" onClick={() => slot.setUrl('')} sx={{ mt: 0.5, fontSize: '0.65rem' }}>清除</Button>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        )}

                        {/* 多模态参考 / 编辑视频:统一一套 image + video + audio 上传组件 */}
                        {(seedanceMode === 'mmref' || seedanceMode === 'edit') && (() => {
                          const imageCap = seedanceMode === 'edit' ? SEEDANCE_EDIT_MAX_IMAGES : SEEDANCE_MAX_REFERENCE_IMAGES;
                          const imageLabel = seedanceMode === 'edit'
                            ? `参考图(可选,最多 ${imageCap} 张)`
                            : `参考图(可选,最多 ${imageCap} 张)`;
                          return (
                            <>
                              {/* 视频上传:edit 模式必填,mmref 模式可选 */}
                              <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                  {seedanceMode === 'edit' ? '待编辑视频 (必填)' : '参考视频 (可选,1 段)'}
                                </Typography>
                                <Box sx={{ width: 220 }}>
                                  <Button
                                    variant="outlined"
                                    component="label"
                                    disabled={uploadingSeedanceVideo}
                                    sx={{
                                      width: '100%', minHeight: 80, display: 'flex', flexDirection: 'column',
                                      alignItems: 'center', justifyContent: 'center',
                                      border: '2px dashed #e2e8f0', borderRadius: 2, color: 'text.secondary',
                                      overflow: 'hidden', p: 0,
                                      '&:hover': { border: '2px dashed #7c3aed', bgcolor: 'rgba(124, 58, 237, 0.04)' }
                                    }}
                                  >
                                    {seedanceVideoUrl ? (
                                      <Box component="video" src={seedanceVideoUrl}
                                        sx={{ width: '100%', maxHeight: 120, objectFit: 'contain' }} />
                                    ) : uploadingSeedanceVideo ? (
                                      <CircularProgress size={24} sx={{ color: '#7c3aed' }} />
                                    ) : (
                                      <>
                                        <VideoLibraryIcon sx={{ fontSize: 28, mb: 0.5, color: '#cbd5e1' }} />
                                        <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                                          {seedanceMode === 'edit' ? '上传视频' : '上传视频(可选)'}
                                        </Typography>
                                      </>
                                    )}
                                    <input type="file" accept="video/*" hidden onChange={async (e) => {
                                      const file = e.target.files?.[0];
                                      if (!file) return;
                                      setUploadingSeedanceVideo(true);
                                      try {
                                        const url = await uploadReferenceImage(file);
                                        if (!url) throw new Error('上传返回为空');
                                        setSeedanceVideoUrl(url);
                                        setSnackbarMsg('视频上传成功');
                                        setSnackbarOpen(true);
                                      } catch (err: any) {
                                        setSnackbarMsg(err.message || '视频上传失败');
                                        setSnackbarOpen(true);
                                      } finally {
                                        setUploadingSeedanceVideo(false);
                                        e.target.value = '';
                                      }
                                    }} />
                                  </Button>
                                  {seedanceVideoUrl && (
                                    <Button size="small" onClick={() => setSeedanceVideoUrl('')} sx={{ mt: 0.5, fontSize: '0.7rem' }}>清除视频</Button>
                                  )}
                                </Box>
                              </Box>

                              {/* 参考图(两种模式都是可选,只是上限不同) */}
                              <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                  {imageLabel}
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: 110, mb: 1 }}>
                                  <Button
                                    variant="outlined"
                                    component="label"
                                    disabled={uploadingSeedanceRef || seedanceRefImages.length >= imageCap}
                                    sx={{
                                      width: '100%', aspectRatio: '1/1', display: 'flex', flexDirection: 'column',
                                      alignItems: 'center', justifyContent: 'center',
                                      border: '2px dashed #e2e8f0', borderRadius: 2, color: 'text.secondary',
                                      overflow: 'hidden', p: 0,
                                      '&:hover': { border: '2px dashed #7c3aed', bgcolor: 'rgba(124, 58, 237, 0.04)' }
                                    }}
                                  >
                                    {uploadingSeedanceRef ? (
                                      <CircularProgress size={24} sx={{ color: '#7c3aed' }} />
                                    ) : (
                                      <>
                                        <AddPhotoAlternateIcon sx={{ fontSize: 32, mb: 0.5, color: '#cbd5e1' }} />
                                        <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                                          {seedanceRefImages.length >= imageCap ? '已达上限' : `上传 (${seedanceRefImages.length}/${imageCap})`}
                                        </Typography>
                                      </>
                                    )}
                                    <input type="file" accept="image/*" hidden onChange={async (e) => {
                                      const file = e.target.files?.[0];
                                      if (!file) return;
                                      if (seedanceRefImages.length >= imageCap) {
                                        setSnackbarMsg(`最多上传 ${imageCap} 张参考图`);
                                        setSnackbarOpen(true);
                                        e.target.value = '';
                                        return;
                                      }
                                      setUploadingSeedanceRef(true);
                                      try {
                                        const url = await uploadReferenceImage(file);
                                        if (!url) throw new Error('上传返回为空');
                                        setSeedanceRefImages(prev => {
                                          const next = [...prev, url];
                                          return next.length <= imageCap ? next : prev;
                                        });
                                        setSnackbarMsg('参考图上传成功');
                                        setSnackbarOpen(true);
                                      } catch (err: any) {
                                        setSnackbarMsg(err.message || '上传失败');
                                        setSnackbarOpen(true);
                                      } finally {
                                        setUploadingSeedanceRef(false);
                                        e.target.value = '';
                                      }
                                    }} />
                                  </Button>
                                </Box>
                                {seedanceRefImages.length > 0 && (
                                  <Box display="flex" flexWrap="wrap" gap={1}>
                                    {seedanceRefImages.map((img, idx) => (
                                      <Box key={idx} sx={{
                                        position: 'relative',
                                        width: 60, height: 60, borderRadius: 1, overflow: 'hidden',
                                        border: '1px solid #e2e8f0',
                                        '&:hover .delete-btn': { opacity: 1 }
                                      }}>
                                        <Box component="img" src={img} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        <IconButton
                                          className="delete-btn"
                                          size="small"
                                          onClick={() => setSeedanceRefImages(prev => prev.filter((_, i) => i !== idx))}
                                          sx={{
                                            position: 'absolute', top: 2, right: 2,
                                            bgcolor: 'rgba(0,0,0,0.5)', color: 'white', p: 0.25,
                                            opacity: 0, transition: 'opacity 0.2s',
                                            '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                                          }}
                                        >
                                          <CloseIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                      </Box>
                                    ))}
                                  </Box>
                                )}
                              </Box>

                              {/* 参考音频(两种模式都可选) */}
                              <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                  参考音频 (可选,1 段。Prompt 里可写「视频配音为音频1」)
                                </Typography>
                                <Box sx={{ width: 220 }}>
                                  <Button
                                    variant="outlined"
                                    component="label"
                                    disabled={uploadingSeedanceAudio}
                                    sx={{
                                      width: '100%', minHeight: 56, display: 'flex',
                                      alignItems: 'center', justifyContent: 'center',
                                      border: '2px dashed #e2e8f0', borderRadius: 2, color: 'text.secondary',
                                      overflow: 'hidden', p: 1,
                                      '&:hover': { border: '2px dashed #7c3aed', bgcolor: 'rgba(124, 58, 237, 0.04)' }
                                    }}
                                  >
                                    {uploadingSeedanceAudio ? (
                                      <CircularProgress size={20} sx={{ color: '#7c3aed' }} />
                                    ) : (
                                      <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                                        {seedanceAudioUrl ? '✓ 音频已上传(点击替换)' : '上传音频(mp3/wav)'}
                                      </Typography>
                                    )}
                                    <input type="file" accept="audio/*" hidden onChange={async (e) => {
                                      const file = e.target.files?.[0];
                                      if (!file) return;
                                      setUploadingSeedanceAudio(true);
                                      try {
                                        const url = await uploadReferenceImage(file);
                                        if (!url) throw new Error('上传返回为空');
                                        setSeedanceAudioUrl(url);
                                        setSnackbarMsg('音频上传成功');
                                        setSnackbarOpen(true);
                                      } catch (err: any) {
                                        setSnackbarMsg(err.message || '音频上传失败');
                                        setSnackbarOpen(true);
                                      } finally {
                                        setUploadingSeedanceAudio(false);
                                        e.target.value = '';
                                      }
                                    }} />
                                  </Button>
                                  {seedanceAudioUrl && (
                                    <Button size="small" onClick={() => setSeedanceAudioUrl('')} sx={{ mt: 0.5, fontSize: '0.7rem' }}>清除音频</Button>
                                  )}
                                </Box>
                              </Box>
                            </>
                          );
                        })()}
                      </>
                    ) : currentModule === 'nano' ? (
                      /* Nano Banana 表单字段 */
                      <>
                        <FormControl fullWidth size="small">
                          <InputLabel>Model</InputLabel>
                          <Select
                            value={nanoModel}
                            label="Model"
                            onChange={(e) => setNanoModel(e.target.value)}
                          >
                            <MenuItem value="nano-banana-2">Nano Banana 2</MenuItem>
                            <MenuItem value="nano-banana">Nano Banana</MenuItem>
                          </Select>
                        </FormControl>

                        <FormControl fullWidth size="small">
                          <InputLabel>Aspect Ratio</InputLabel>
                          <Select
                            value={aspectRatio}
                            label="Aspect Ratio"
                            onChange={(e) => setAspectRatio(e.target.value)}
                          >
                            <MenuItem value="16:9">16:9</MenuItem>
                            <MenuItem value="9:16">9:16</MenuItem>
                            <MenuItem value="1:1">1:1</MenuItem>
                            <MenuItem value="4:3">4:3</MenuItem>
                            <MenuItem value="3:4">3:4</MenuItem>
                          </Select>
                        </FormControl>

                        {nanoModel === 'nano-banana-2' && (
                          <FormControl fullWidth size="small">
                            <InputLabel>Image Size</InputLabel>
                            <Select
                              value={nanoImageSize}
                              label="Image Size"
                              onChange={(e) => setNanoImageSize(e.target.value)}
                            >
                              <MenuItem value="1K">1K</MenuItem>
                              <MenuItem value="2K">2K</MenuItem>
                              <MenuItem value="4K">4K</MenuItem>
                            </Select>
                          </FormControl>
                        )}

                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>参考图 (Reference Images)</Typography>

                          {/* 上传按钮：大正方形，图片列表在下方 */}
                          <Box>
                            {/* 上传按钮 */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: 110, mb: 1 }}>
                              <Button
                                variant="outlined"
                                component="label"
                                disabled={uploadingNano}
                                sx={{
                                  width: '100%',
                                  aspectRatio: '1/1',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: '2px dashed #e2e8f0',
                                  borderRadius: 2,
                                  color: 'text.secondary',
                                  overflow: 'hidden',
                                  p: 0,
                                  '&:hover': {
                                    border: '2px dashed #f59e0b',
                                    bgcolor: 'rgba(245, 158, 11, 0.04)'
                                  }
                                }}
                              >
                                {uploadingNano ? (
                                  <CircularProgress size={24} sx={{ color: '#f59e0b' }} />
                                ) : (
                                  <>
                                    <AddPhotoAlternateIcon sx={{ fontSize: 32, mb: 0.5, color: '#cbd5e1' }} />
                                    <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                                      上传图片
                                    </Typography>
                                  </>
                                )}
                                <input type="file" accept="image/*" hidden onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  setUploadingNano(true);
                                  try {
                                    const url = await uploadReferenceImage(file);
                                    setNanoImages([...nanoImages, url]);
                                    setSnackbarMsg('参考图上传成功');
                                    setSnackbarOpen(true);
                                  } catch (err: any) {
                                    setSnackbarMsg(err.message || '上传失败');
                                    setSnackbarOpen(true);
                                  } finally {
                                    setUploadingNano(false);
                                    e.target.value = '';
                                  }
                                }} />
                              </Button>
                            </Box>

                            {/* 图片列表：下方小图 */}
                            <Box display="flex" flexWrap="wrap" gap={1}>
                              {nanoImages.map((img, idx) => (
                                <Box key={idx} sx={{
                                  position: 'relative',
                                  width: 60,
                                  height: 60,
                                  borderRadius: 1,
                                  overflow: 'hidden',
                                  border: '1px solid #e2e8f0',
                                  '&:hover .delete-btn': { opacity: 1 }
                                }}>
                                  <Box component="img" src={img} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                  <IconButton
                                    className="delete-btn"
                                    size="small"
                                    onClick={() => {
                                      const newImages = [...nanoImages];
                                      newImages.splice(idx, 1);
                                      setNanoImages(newImages);
                                    }}
                                    sx={{
                                      position: 'absolute',
                                      top: 2,
                                      right: 2,
                                      bgcolor: 'rgba(0,0,0,0.5)',
                                      color: 'white',
                                      p: 0.25,
                                      opacity: 0,
                                      transition: 'opacity 0.2s',
                                      '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                                    }}
                                  >
                                    <CloseIcon sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        </Box>
                      </>
                    ) : (
                      /* GPT Image 表单字段 */
                      <>
                        <FormControl fullWidth size="small">
                          <InputLabel>Model</InputLabel>
                          <Select
                            value="gpt-image-1"
                            label="Model"
                            disabled
                          >
                            <MenuItem value="gpt-image-1">GPT-Image-2</MenuItem>
                          </Select>
                        </FormControl>

                        <FormControl fullWidth size="small">
                          <InputLabel>Size</InputLabel>
                          <Select
                            value={gptSize}
                            label="Size"
                            onChange={(e) => setGptSize(e.target.value)}
                          >
                            <MenuItem value="auto">auto (自适应)</MenuItem>
                            <MenuItem value="1:1">1:1</MenuItem>
                            <MenuItem value="3:2">3:2</MenuItem>
                            <MenuItem value="2:3">2:3</MenuItem>
                            <MenuItem value="16:9">16:9</MenuItem>
                            <MenuItem value="9:16">9:16</MenuItem>
                            <MenuItem value="4:3">4:3</MenuItem>
                            <MenuItem value="3:4">3:4</MenuItem>
                            <MenuItem value="21:9">21:9</MenuItem>
                            <MenuItem value="9:21">9:21</MenuItem>
                            <MenuItem value="1:3">1:3</MenuItem>
                            <MenuItem value="3:1">3:1</MenuItem>
                            <MenuItem value="2:1">2:1</MenuItem>
                            <MenuItem value="1:2">1:2</MenuItem>
                          </Select>
                        </FormControl>

                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>参考图 (Reference Images)</Typography>

                          {/* 上传按钮：大正方形，图片列表在下方 */}
                          <Box>
                            {/* 上传按钮 */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: 110, mb: 1 }}>
                              <Button
                                variant="outlined"
                                component="label"
                                disabled={uploadingGpt}
                                sx={{
                                  width: '100%',
                                  aspectRatio: '1/1',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: '2px dashed #e2e8f0',
                                  borderRadius: 2,
                                  color: 'text.secondary',
                                  overflow: 'hidden',
                                  p: 0,
                                  '&:hover': {
                                    border: '2px dashed #6366f1',
                                    bgcolor: 'rgba(99, 102, 241, 0.04)'
                                  }
                                }}
                              >
                                {uploadingGpt ? (
                                  <CircularProgress size={24} sx={{ color: '#6366f1' }} />
                                ) : (
                                  <>
                                    <AddPhotoAlternateIcon sx={{ fontSize: 32, mb: 0.5, color: '#cbd5e1' }} />
                                    <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                                      上传图片
                                    </Typography>
                                  </>
                                )}
                                <input type="file" accept="image/*" hidden onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  setUploadingGpt(true);
                                  try {
                                    const url = await uploadReferenceImage(file);
                                    setGptImages([...gptImages, url]);
                                    setSnackbarMsg('参考图上传成功');
                                    setSnackbarOpen(true);
                                  } catch (err: any) {
                                    setSnackbarMsg(err.message || '上传失败');
                                    setSnackbarOpen(true);
                                  } finally {
                                    setUploadingGpt(false);
                                    e.target.value = '';
                                  }
                                }} />
                              </Button>
                            </Box>

                            {/* 图片列表：下方小图 */}
                            <Box display="flex" flexWrap="wrap" gap={1}>
                              {gptImages.map((img, idx) => (
                                <Box key={idx} sx={{
                                  position: 'relative',
                                  width: 60,
                                  height: 60,
                                  borderRadius: 1,
                                  overflow: 'hidden',
                                  border: '1px solid #e2e8f0',
                                  '&:hover .delete-btn': { opacity: 1 }
                                }}>
                                  <Box component="img" src={img} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                  <IconButton
                                    className="delete-btn"
                                    size="small"
                                    onClick={() => {
                                      const newImages = [...gptImages];
                                      newImages.splice(idx, 1);
                                      setGptImages(newImages);
                                    }}
                                    sx={{
                                      position: 'absolute',
                                      top: 2,
                                      right: 2,
                                      bgcolor: 'rgba(0,0,0,0.5)',
                                      color: 'white',
                                      p: 0.25,
                                      opacity: 0,
                                      transition: 'opacity 0.2s',
                                      '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                                    }}
                                  >
                                    <CloseIcon sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        </Box>
                      </>
                    )}

                    {wuyinkejiGrokBlocked && (
                      <Box sx={{
                        mb: 1.5,
                        px: 1.5,
                        py: 1,
                        borderRadius: 1,
                        bgcolor: 'rgba(245, 158, 11, 0.12)',
                        border: '1px solid rgba(245, 158, 11, 0.4)',
                        color: '#b45309',
                        fontSize: 13,
                        lineHeight: 1.5,
                      }}>
                        ⚠️ 该通道正在维护，暂不可用，请改用其他档位或稍后再试
                      </Box>
                    )}
                    <Box display="flex" justifyContent="space-between" alignItems="center" mt="auto" pb={2}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setPrompt('');
                          setImage1('');
                          setImage2('');
                          setImage3('');
                          setSoraUrl('');
                          setGrokUrl('');
                          setGrokImagineImages([]);
                          setGrokStableImages([]);
                          setNanoImages([]);
                          setGptImages([]);
                        }}
                      >
                        Clear
                      </Button>
                      <Button
                        variant="contained"
                        size="large"
                        onClick={handleGenerate}
                        disabled={isSubmitting || wuyinkejiGrokBlocked || (currentModule === 'veo' && activeTab === 'veo2')}
                        sx={{
                          px: 2.5,
                          height: 40,
                          minWidth: 'unset',
                          letterSpacing: 0,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0,
                          background: (wuyinkejiGrokBlocked || (currentModule === 'veo' && activeTab === 'veo2'))
                            ? 'rgba(156, 163, 175, 0.5)'
                            : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                          boxShadow: (wuyinkejiGrokBlocked || (currentModule === 'veo' && activeTab === 'veo2'))
                            ? 'none'
                            : '0 4px 12px rgba(37,99,235,0.3)',
                          ml: 'auto',
                          '&.Mui-disabled': {
                            background: 'rgba(156, 163, 175, 0.5)',
                            color: 'rgba(255, 255, 255, 0.6)',
                          },
                        }}
                      >
                        {isSubmitting ? (
                          <CircularProgress size={20} color="inherit" sx={{ mr: 0.25 }} />
                        ) : (
                          <Box
                            component="img"
                            src="/Xup.svg"
                            alt="generate"
                            sx={{ width: 35, height: 35, filter: 'brightness(0) invert(1)', mr: -0.5 }}
                          />
                        )}
                        <Box component="span" sx={{ fontWeight: 700 }}>
                          {isSubmitting ? '生成中...' : (hasCurrentCost ? `生成（${currentCost}积分）` : '生成')}
                        </Box>
                      </Button>
                    </Box>
                  </Box>
                </Box>
                {/* 右侧结果区域：统一的大容器，包含大图预览和历史记录 */}
                <Box sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  mr: { xs: 0, lg: 3 },
                  height: 'calc(100vh - 200px)',
                  minHeight: 600,
                  position: 'relative',
                  // 悬浮时触发子元素动画
                  '&:hover .border-anim': {
                    // Red: 100% 0% (BL) -> 100% 100% (TL)
                    // Blue: 0% 100% (TR) -> 0% 0% (BR)
                    backgroundPosition: '100% 100%, 0% 0%',
                  }
                }}>
                  {/* 1. 独立边框层：仅显示边框线条，中间镂空，避免影响内部半透明背景 */}
                  <Box className="border-anim" sx={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '24px',
                    padding: '2px', // 边框宽度
                    
                    // 使用两个径向渐变模拟移动的光斑
                    // 1. 红色光斑 (Red Spot)
                    // 2. 蓝色光斑 (Blue Spot)
                    backgroundImage: `
                      radial-gradient(circle at center, #ef4444 0%, transparent 50%),
                      radial-gradient(circle at center, #3b82f6 0%, transparent 50%)
                    `,
                    backgroundSize: '200% 200%, 200% 200%', // 2倍大小，便于计算位置
                    backgroundRepeat: 'no-repeat, no-repeat',
                    
                    // 初始位置 (基于 200% 大小的计算)：
                    // Red (Bottom Left): 100% 0%
                    // Blue (Top Right): 0% 100%
                    backgroundPosition: '100% 0%, 0% 100%',
                    
                    transition: 'background-position 1.5s ease', // 平滑移动动画
                    
                    // 核心：使用 mask 镂空中间区域，只保留 2px 边框
                    mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                    maskComposite: 'exclude',
                    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                    WebkitMaskComposite: 'xor',
                    
                    pointerEvents: 'none',
                    zIndex: 1,
                  }} />

                  {/* 2. 内容层：负责背景、滚动和内容展示 */}
                  <Box sx={{
                    flex: 1,
                    background: 'rgba(170, 210, 255, 0.35)', // 纯净的冰雪背景
                    backdropFilter: 'blur(12px) saturate(110%)',
                    borderRadius: '24px', // 与边框一致
                    overflow: 'hidden', // 裁剪内部滚动条圆角
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    zIndex: 0,
                    boxShadow: 'none',
                  }}>
                    <Box sx={{
                      flex: 1,
                      overflowY: 'auto',
                      p: 3,
                      color: 'white',
                      textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      
                      // 顶部高光装饰
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '1px',
                        background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.8) 50%, transparent 100%)',
                        opacity: 0.7,
                        pointerEvents: 'none'
                      }
                    }}>

                  {/* 统一结果展示区域：Grid 布局 */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: '1fr 1fr 1fr' }, gap: 2 }}>
                    {/* 1. 当前生成任务或最近成功任务（统一隐藏，全部用队列卡片展示，保证三模块表现一致） */}
                    {(() => {
                      // 统一不用单独“当前卡片”，保持三模块一致，仅展示下方队列卡片
                      return null;
                      // 计算最近一个可展示的视频：优先当前 generationResult；否则最近成功的队列任务
                      const latestSucceededJob = jobs.find(j => j.status === 'succeeded' && j.result?.video_url);
                      const hasCurrent = Boolean(generationResult);
                      const videoSrcStr = hasCurrent
                        ? (typeof generationResult?.data?.output === 'string')
                          ? generationResult.data.output
                          : (generationResult?.data?.video_url || generationResult?.video_url)
                        : latestSucceededJob?.result?.video_url;
                      // 非 Sora 标签：按常规逻辑显示当前卡片
                      const showCard = (isGenerating || hasCurrent || Boolean(videoSrcStr));
                      if (!showCard) return null;
                      const modelLabel = (taskId || isGenerating)
                        ? generatingModelRef.current
                        : (generationResult?._model || latestSucceededJob?.model || '');
                      return (
                      <Box sx={{ position: 'relative' }}>
                        <Box sx={{
                          height: 120,
                          borderRadius: 1,
                          overflow: 'hidden',
                          position: 'relative',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: '#1e293b',
                          border: '1px solid rgba(255,255,255,0.06)',
                          '&:hover .model-tag': { opacity: 1 },
                          '&:hover .delete-btn': { opacity: 1 }
                        }}>
                          {/* 当前结果卡片的删除按钮（右上角方形，悬浮变色） */}
                          <IconButton
                            className="delete-btn"
                            aria-label="删除"
                            onClick={() => {
                              // 取消当前查询与UI显示
                              // 1) 停止轮询
                              if (pollTimerRef.current) {
                                clearInterval(pollTimerRef.current);
                                pollTimerRef.current = null;
                              }
                              pollingActiveRef.current = false;
                              // 2) 清除当前任务状态
                              setTaskId(null);
                              setIsGenerating(false);
                              // 3) 清空当前结果卡片
                              setGenerationResult(null);
                              // 4) 可选：标记队列中运行的任务为已取消（不强制删除，避免历史丢失）
                              setJobs(prev => prev.map(j => j.status === 'running' && j.taskId ? { ...j, status: 'failed', error: '用户已取消' } : j));
                              // 5) 反馈
                              setSnackbarMsg('已取消查询');
                              setSnackbarOpen(true);
                            }}
                            sx={{
                              position: 'absolute',
                              top: 4,
                              right: 4,
                              width: 16,
                              height: 16,
                              minWidth: 0,
                              padding: 0,
                              borderRadius: 1,
                              bgcolor: 'rgba(0,0,0,0.5)',
                              color: 'white',
                              border: '1px solid rgba(255,255,255,0.2)',
                              zIndex: 3,
                              opacity: 0,
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                bgcolor: 'rgba(239,68,68,0.8)',
                                borderColor: 'rgba(255,255,255,0.35)'
                              }
                            }}
                          >
                            <CloseIcon sx={{ fontSize: 12 }} />
                          </IconButton>
                          {/* 模型标签 */}
                          {modelLabel && (
                            <Chip
                              className="model-tag"
                              label={modelLabel}
                              size="small"
                              sx={{
                                position: 'absolute',
                                top: 8,
                                left: 8,
                                zIndex: 2,
                                bgcolor: 'rgba(0,0,0,0.6)',
                                color: 'white',
                                backdropFilter: 'blur(4px)',
                                height: 20,
                                borderRadius: '4px',
                                opacity: 0,
                                transition: 'opacity 0.2s',
                                '& .MuiChip-label': { px: 0.8, fontSize: '0.7rem', fontWeight: 600 }
                              }}
                            />
                          )}

                          {(Boolean(taskId) && !generationResult) ? (
                            <Box
                              sx={{
                                position: 'relative',
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                '&::after': {
                                  content: '""',
                                  position: 'absolute',
                                  inset: 0,
                                  background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0) 100%)',
                                  transform: 'skewX(-20deg)',
                                  animation: `${shine} 1.8s linear infinite`,
                                }
                              }}
                            >
                              {typeof currentProgress === 'number' ? (
                                <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 2 }}>
                                  <Typography sx={{ fontSize: 12, fontWeight: 'bold', color: 'white' }}>{currentProgress}%</Typography>
                                </Box>
                              ) : null}
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  position: 'relative',
                                  zIndex: 1,
                                  color: 'rgba(255,255,255,0.85)',
                                  letterSpacing: 1,
                                  fontSize: 12,
                                  animation: `${blink} 2.4s ease-in-out infinite`,
                                }}
                              >
                                正在生成...
                              </Typography>
                            </Box>
                          ) : (
                            <>
                              {generationResult?.data && generationResult.data[0]?.url ? (
                                <img
                                  src={generationResult.data[0].url}
                                  style={{ height: '100%', width: '100%', objectFit: 'cover' }}
                                  alt="Generated"
                                />
                              ) : videoSrcStr ? (
                                <VideoWithLoader style={{ height: '100%', width: '100%', objectFit: 'cover' }} src={videoSrcStr} />
                              ) : (
                                <Box textAlign="center" p={1}>
                                  <Typography variant="caption" color="success.main">完成</Typography>
                                </Box>
                              )}
                            </>
                          )}
                        </Box>
                      </Box>
                      );
                    })()}

                    {/* 2. 历史记录卡片（在任意模型下显示，不再按模块过滤） */}
                    {(() => {
                      // 基于 URL 去重，避免同一视频/图片重复显示
                      const seen = new Set();
                      // 当存在“当前生成卡片”时，避免列表再展示进行中的占位（queued/submitting/running）
                      const isShowingCurrentCard = isGenerating || Boolean(generationResult);
                      const deduped = jobs
                        // 不再按模块过滤，所有模块（veo/sora/nano）的历史都展示
                        .filter(() => true)
                        // 过滤 Nano/GPT 的失败任务；Sora 失败保留并显示上游原因
                        .filter(j => !((j.module === 'nano' || j.module === 'gpt') && j.status === 'failed'))
                        // 对已成功的直链做去重
                        .filter(j => {
                        const url = j.result?.video_url || j.result?.image_url || '';
                          if (!url) return true; // 保留已完成但尚未解析到直链的记录（若存在）
                        const key = String(url);
                        if (seen.has(key)) return false;
                        seen.add(key);
                        return true;
                      });
                      return deduped.map((job) => (
                      <Box key={job.id} sx={{ position: 'relative' }}>
                          <Box sx={{
                          height: 120,
                          borderRadius: 1,
                          overflow: 'hidden',
                          position: 'relative',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: '#1e293b',
                          border: '1px solid rgba(255,255,255,0.06)',
                            '&:hover .model-tag': { opacity: 1 },
                            '&:hover .delete-btn': { opacity: 1 }
                        }}>
                               {/* 删除按钮（右上角方形，悬浮变色） */}
                           <IconButton
                             className="delete-btn"
                                 aria-label="删除"
                                 onClick={() => {
                                   // 全局同步删除：同一资源（相同 video_url / image_url）的所有卡片一起移除
                                   const keyUrl = job.result?.video_url || job.result?.image_url || '';
                                   setJobs(prev => {
                                     const filtered = prev.filter(j => {
                                       const url = j.result?.video_url || j.result?.image_url || '';
                                       if (keyUrl) return url !== keyUrl; // 删除同源资源
                                       return j.id !== job.id; // 无直链则按 id 删除
                                     });
                                     // 如果删除后队列为空，同时清除 generationResult 和 localStorage，让空状态显示
                                     if (filtered.length === 0) {
                                       setGenerationResult(null);
                                       localStorage.removeItem('vwin_last_generation_result');
                                       localStorage.removeItem('vwin_jobs');
                                     }
                                     return filtered;
                                   });
                                 }}
                                 sx={{
                                   position: 'absolute',
                                   top: 4,
                                   right: 4,
                                   width: 16,
                                   height: 16,
                                   minWidth: 0,
                                   padding: 0,
                                   borderRadius: 1, // 方形
                                   bgcolor: 'rgba(0,0,0,0.5)',
                                   color: 'white',
                                   border: '1px solid rgba(255,255,255,0.2)',
                               zIndex: 3,
                               opacity: 0,
                                   transition: 'all 0.2s ease',
                                   '&:hover': {
                                     bgcolor: 'rgba(239,68,68,0.8)', // 红色高亮
                                     borderColor: 'rgba(255,255,255,0.35)'
                                   }
                                 }}
                               >
                                 <CloseIcon sx={{ fontSize: 12 }} />
                               </IconButton>
                          {/* 模型标签：仅在生成完成后展示 */}
                          {job.status === 'succeeded' && (
                            <Chip
                              className="model-tag"
                              label={
                                job.module === 'sora'
                                  ? 'sora new'
                                  : job.module === 'grok'
                                    ? 'grok'
                                    : job.model
                              }
                              size="small"
                              sx={{
                                position: 'absolute',
                                top: 8,
                                left: 8,
                                zIndex: 2,
                                bgcolor: 'rgba(0,0,0,0.6)',
                                color: 'white',
                                backdropFilter: 'blur(4px)',
                                height: 20,
                                borderRadius: '4px',
                                opacity: 0,
                                transition: 'opacity 0.2s',
                                '& .MuiChip-label': { px: 0.8, fontSize: '0.7rem', fontWeight: 600 }
                              }}
                            />
                          )}

                          {job.result?.video_url ? (
                            <VideoWithLoader
                              style={{ height: '100%', width: '100%', objectFit: 'cover' }}
                              src={job.result.video_url}
                            />
                          ) : job.result?.image_url ? (
                            <img src={job.result.image_url} alt="result" style={{ height: '100%', width: '100%', objectFit: 'cover' }} />
                          ) : (
                            <Box
                              sx={{
                                position: 'relative',
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                '&::after': {
                                  content: '""',
                                  position: 'absolute',
                                  inset: 0,
                                  background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0) 100%)',
                                  transform: 'skewX(-20deg)',
                                  animation: `${shine} 1.8s linear infinite`,
                                }
                              }}
                            >
                              {typeof job.progress === 'number' ? (
                                <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 2 }}>
                                  <Typography sx={{ fontSize: 12, fontWeight: 'bold', color: 'white' }}>{job.progress}%</Typography>
                                </Box>
                              ) : null}
                              {job.status === 'failed' ? (
                                <Typography
                                  variant="caption"
                                  title={job.error || ''}
                                  sx={{
                                    position: 'relative',
                                    zIndex: 1,
                                    color: 'error.main',
                                    letterSpacing: 0.3,
                                    fontWeight: 600,
                                    background: 'rgba(239,68,68,0.15)',
                                    border: '1px solid rgba(239,68,68,0.5)',
                                    borderRadius: 1,
                                    px: 1.5,
                                    py: 0.5,
                                    mx: 1,
                                    maxWidth: '90%',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 3,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    textAlign: 'center'
                                  }}
                                >
                                  {job.error ? `生成失败：${job.error}` : '视频生成失败'}
                                </Typography>
                              ) : job.status === 'succeeded' ? (
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    position: 'relative',
                                    zIndex: 1,
                                    color: 'success.main',
                                    letterSpacing: 0.5,
                                    fontWeight: 600,
                                    background: 'rgba(16,185,129,0.15)',
                                    border: '1px solid rgba(16,185,129,0.5)',
                                    borderRadius: 1,
                                    px: 1.5,
                                    py: 0.5
                                  }}
                                >
                                  完成
                                </Typography>
                              ) : (
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    position: 'relative',
                                    zIndex: 1,
                                    color: 'rgba(255,255,255,0.85)',
                                    letterSpacing: 1,
                                    fontSize: 12,
                                    animation: `${blink} 2.4s ease-in-out infinite`,
                                  }}
                                >
                                  正在生成...
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                      </Box>
                  ));
                })()}
                  </Box>

                  {/* 3. Empty State (既没生成，也没历史) */}
                  {!isGenerating && !generationResult && (!jobs || jobs.length === 0) && (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', opacity: 0.8, minHeight: 400 }}>
                      <Typography variant="h5" fontWeight={900} letterSpacing={2} sx={{ 
                        color: 'white',
                        WebkitTextStroke: '1px black',
                        textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }}>RESULT</Typography>
                      <Typography variant="body1" sx={{ 
                        mt: 1,
                        color: 'white',
                        WebkitTextStroke: '0.5px black',
                        fontWeight: 500
                      }}>Your result will appear here</Typography>
                    </Box>
                  )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Box>
        </Box>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={snackbarMsg.includes('失败') ? 12000 : 6000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarMsg.includes('失败') ? 'error' : 'info'}
            sx={{ width: '100%', maxWidth: 520, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          >
            {snackbarMsg}
          </Alert>
        </Snackbar>

        {/* 积分检查弹窗 */}
        <PointsCheckDialog
          open={showDialog}
          onClose={closeDialog}
          isZeroPoints={dialogProps.isZeroPoints}
        />
      </Box>
    </ThemeProvider>
  );
};

export default HomePage;
